<template>
  <section id="SavedCarts">
    <div id="acctOrders">
      <div class="container">
        <!--
          Two-column layout: SavedCarts tabbed content on the left,
          shared CartWidget (standalone mode) on the right. Pattern
          matches the OrderHistory integration: cart column collapses
          to zero-width (oh-cart-col-hidden) when the widget signals
          its inline tray isn't visible (cart empty, or user has
          collapsed to side pill), and the left column stretches back
          to col-12 so the tabs fill the row.

          Widget is never unmounted — when the inline tray is hidden,
          the widget stays alive inside the zero-width host and keeps
          rendering its portal'd side pill (which lives in App.vue
          via <portal-target name="cart-pill">).
        -->
        <!-- Cart is now a global floating panel (mounted in App.vue),
             so this page no longer hosts an inline cart column; the
             content spans the full width. -->
        <div class="row">
          <div class="col-12">
        <div id="ao-table">
          <div class="row align-items-center mb-4">
             <div class="col-lg-12">
               <h2>Your Open Carts &amp; History</h2>
             </div>
          </div>

          <!-- ===== TABS =====
               Saved tab is the original SavedCarts content
               (in-progress carts of either Cart or Rfq
               discriminator). Orders + Quotes tabs surface
               summarized history pulled from the same
               endpoints OrderHistory.vue and RFQHistory.vue
               use, with their per-row actions mirrored into
               compact buttons. Each non-Saved tab carries a
               "See full history" link that routes the user
               to the original page for filtering / paging /
               full feature parity. -->
          <!-- Tab strip: tabs on the left, admin-only "All
               Companies" toggle on the right (gated on
               isMasterUser). The wrapper is a flex row with
               space-between so the toggle floats right while
               the tabs hug the left. -->
          <div class="savedcarts-tabs-row">
            <div class="savedcarts-tabs">
              <button type="button"
                      class="savedcarts-tab"
                      :class="{ 'is-active': activeTab === 'saved' }"
                      @click="setTab('saved')">
                Saved
                <span class="savedcarts-tab-count" v-if="carts.length > 0">
                  {{ carts.length }}
                </span>
              </button>
              <button type="button"
                      class="savedcarts-tab"
                      :class="{ 'is-active': activeTab === 'orders' }"
                      @click="setTab('orders')">
                Orders
                <span class="savedcarts-tab-count" v-if="historyOrdersCount > 0">
                  {{ historyOrdersCount }}
                </span>
              </button>
              <button type="button"
                      class="savedcarts-tab"
                      :class="{ 'is-active': activeTab === 'quotes' }"
                      @click="setTab('quotes')">
                Quotes
                <span class="savedcarts-tab-count" v-if="historyQuotesCount > 0">
                  {{ historyQuotesCount }}
                </span>
              </button>
            </div>

            <!-- Admin toggle. Only renders for master/admin
                 users. Two-state pill: "My Company" (default)
                 vs "All Companies". Flipping triggers fresh
                 fetches across all loaded tabs (see watcher in
                 the script section). -->
            <div class="savedcarts-admin-toggle"
                 v-if="isMasterUser"
                 :title="allCompaniesMode
                   ? 'Showing data for all companies. Click to filter to your selected company.'
                   : 'Showing data for your selected company. Click to view all companies.'">
              <button type="button"
                      class="savedcarts-toggle-btn"
                      :class="{ 'is-active': !allCompaniesMode }"
                      @click="allCompaniesMode = false">
                My Company
              </button>
              <button type="button"
                      class="savedcarts-toggle-btn"
                      :class="{ 'is-active': allCompaniesMode }"
                      @click="allCompaniesMode = true">
                All Companies
              </button>
            </div>

            <!-- Customer scope toggle. Only for non-admins (admins use the
                 All Companies toggle above). Mirrors the dashboard's
                 "All account orders / Just mine" control so the whole-account
                 view carries through from the dashboard into the history. -->
            <div class="savedcarts-admin-toggle"
                 v-if="!isMasterUser"
                 :title="accountScope === 'account'
                   ? 'Showing everyone on this account. Click Just mine to see only your own.'
                   : 'Showing only orders you placed. Click All account orders to see everyone.'">
              <button type="button"
                      class="savedcarts-toggle-btn"
                      :class="{ 'is-active': accountScope === 'account' }"
                      @click="switchAccountScope('account')">
                All account orders
              </button>
              <button type="button"
                      class="savedcarts-toggle-btn"
                      :class="{ 'is-active': accountScope === 'mine' }"
                      @click="switchAccountScope('mine')">
                Just mine
              </button>
            </div>
          </div>

          <!-- No company selected (and not in All Companies mode): hide all
               tab content and show the shared select-a-company prompt once,
               regardless of which tab is active. Replaces the three per-tab
               "Please select a company…" messages. -->
          <select-company-prompt
            v-if="!companySelected"
            :user-name="currentUser && currentUser.firstName ? currentUser.firstName : 'Admin'" />

          <!-- ===== SAVED TAB =====
               Original SavedCarts content, untouched. -->
          <div v-show="activeTab === 'saved'" v-if="companySelected">
          <div class="row" v-if="loading">
            <div class="col-12 mt-4"><h3>Loading saved carts...</h3></div>
          </div>

          <div class="row" v-else-if="!carts || carts.length === 0">
            <div class="col-12 mt-4"><h3>You have no saved carts.</h3></div>
          </div>

          <div class="row mb-3" v-if="!loading && carts && carts.length > 0">
  <div class="col-lg-7">
    <label class="clear-date-label">Clear carts before:</label>
    <div class="clear-date-row">
      <date-picker
        v-model="clearBeforeDate"
        format="MM/DD/YYYY"
        placeholder="Select Date"
        :disabled-date="disabledDate"
        class="clear-date-picker"
      ></date-picker>
      <!-- Single context-aware Clear button: deletes checked rows if any,
           else carts before the date if one is set, else all — each with
           its own confirm. Label/title reflect which it will do. -->
      <button
        class="btn btn-danger btn-sm ml-2 compact-action-btn"
        @click="onUnifiedClear"
        :title="clearButtonTitle"
      >
        {{ clearButtonLabel }}
      </button>
    </div>
  </div>
</div>

          <b-table
            v-if="!loading && carts && carts.length > 0"
            id="savedCartsTable"
            stacked="lg"
            :fields="cartFields"
            :items="carts"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :tbody-tr-class="savedTrClass"
            responsive="sm"
          >
            <!-- ===== Layer-1 cells (always visible) ===== -->
            <!-- Select column (first): select-all in header, per-row below. -->
            <template v-slot:head(select)>
              <input type="checkbox"
                     class="cart-select-all"
                     v-model="allCartsSelected"
                     :indeterminate.prop="someCartsSelected"
                     title="Select all carts" />
            </template>
            <template v-slot:cell(select)="data">
              <input type="checkbox"
                     class="cart-select-row"
                     :value="data.item.id"
                     v-model="selectedCartIds"
                     @click.stop
                     aria-label="Select this cart" />
            </template>
            <template v-slot:cell(expand)="row">
              <button type="button"
                      class="cart-expand-btn"
                      :class="{ 'is-open': row.detailsShowing }"
                      :aria-label="row.detailsShowing ? 'Hide details' : 'Show details'"
                      :title="row.detailsShowing ? 'Hide details' : 'Show details'"
                      @click="expandSavedCart(row.item, row)">
                <span class="cart-expand-icon">{{ row.detailsShowing ? '−' : '+' }}</span>
              </button>
            </template>

            <template v-slot:cell(dateCreated)="data">
              {{ data.item.dateCreated | formatDate }}
            </template>

            <template v-slot:cell(type)="data">
              {{ getCartTypeLabel(data.item) }}
            </template>

            <template v-slot:cell(poNumbers)="data">
              <span v-if="data.item.poNumbers && data.item.poNumbers.length > 0"
                    class="cart-po-list">
                <span class="cart-po-chip"
                      v-for="(po, idx) in data.item.poNumbers"
                      :key="'po-' + data.item.id + '-' + idx + '-' + po">
                  {{ po }}
                </span>
              </span>
              <span v-else class="cart-po-empty">—</span>
            </template>

            <template v-slot:cell(itemCount)="data">
              {{ data.item.itemCount }}
            </template>

            <template v-slot:cell(grandTotal)="data">
              <span class="cart-total-cell">
                {{ (data.item.grandTotal || 0) | currency }}
              </span>
            </template>

            <template v-slot:cell(actions)="data">
              <div class="cart-actions">
                <!-- ACTIVE-CART INDICATOR
                     When this saved cart is the user's
                     currently loaded active cart, swap the
                     Resume action for a disabled "In Cart"
                     badge. Resuming an already-resumed cart
                     is a no-op visually but confusing
                     (button does "something" but the user
                     is already on the right state) — better
                     to communicate the state explicitly. -->
                <span v-if="isActiveCart(data.item)"
                      class="cart-incart-badge"
                      title="This cart is currently loaded in your active cart">
                  <svg class="cart-icon-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="16" height="16">
                    <path fill="#777" d="M0 24C0 10.7 10.7 0 24 0H69.5c22 0 41.5 12.8 50.6 32h411c26.3 0 45.5 25 38.6 50.4l-41 152.3c-8.5 31.4-37 53.3-69.5 53.3H170.7l5.4 28.5c2.2 11.3 12.1 19.5 23.6 19.5H488c13.3 0 24 10.7 24 24s-10.7 24-24 24H199.7c-34.6 0-64.3-24.6-70.7-58.5L77.4 54.5c-.7-3.8-4-6.5-7.9-6.5H24C10.7 48 0 37.3 0 24zM128 464a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm336-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/>
                  </svg>
                  In Cart
                </span>
                <a v-else href="#" class="cart-resume-link" @click.prevent="resumeCart(data.item)">
                  <svg class="cart-icon-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="16" height="16">
                    <path fill="#c0392b" d="M0 24C0 10.7 10.7 0 24 0H69.5c22 0 41.5 12.8 50.6 32h411c26.3 0 45.5 25 38.6 50.4l-41 152.3c-8.5 31.4-37 53.3-69.5 53.3H170.7l5.4 28.5c2.2 11.3 12.1 19.5 23.6 19.5H488c13.3 0 24 10.7 24 24s-10.7 24-24 24H199.7c-34.6 0-64.3-24.6-70.7-58.5L77.4 54.5c-.7-3.8-4-6.5-7.9-6.5H24C10.7 48 0 37.3 0 24zM128 464a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm336-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/>
                  </svg>
                  Resume
                </a>
                <a href="#" class="cart-delete-link" @click.prevent="confirmDeleteCart(data.item)" title="Delete Cart">
                  &times;
                </a>
              </div>
            </template>

            <!-- ===== Layer-2 details panel (expanded on +) =====
                 Two sections:
                 1) Cart metadata (Company / Code) — quick
                    reference for which company this cart was
                    built under.
                 2) Order breakdown — Orders > Assemblies >
                    Parts, identical hierarchy to /cart so the
                    user can identify exactly what's about to
                    be restored before clicking Resume. Lazy
                    loaded from getCart on first expand;
                    cached in cartDetails for instant re-open.
                 -->
            <template v-slot:row-details="row">
              <div class="cart-details-panel">
                <dl class="cart-details-list">
                  <div class="cart-details-pair">
                    <dt>Company</dt>
                    <dd>{{ row.item.companyName || "—" }}</dd>
                  </div>
                  <div class="cart-details-pair">
                    <dt>Code</dt>
                    <dd>{{ row.item.companyCode || "—" }}</dd>
                  </div>
                </dl>

                <!-- Order > Assembly > Parts breakdown.
                     States:
                     - loading: brief skeleton text while
                       getCart is in flight
                     - error: surface the failure but keep
                       the row open so the user can retry
                       (collapse + re-expand kicks off a new
                       fetch since the cache entry is reset)
                     - empty: no purchaseOrders on the cart
                       (rare but possible for empty carts)
                     - data: the hierarchical card list -->
                <div class="saved-cart-orders">
                  <div v-if="cartDetails[String(row.item.id)] && cartDetails[String(row.item.id)].loading"
                       class="saved-cart-orders-loading">
                    Loading cart contents…
                  </div>
                  <div v-else-if="cartDetails[String(row.item.id)] && cartDetails[String(row.item.id)].error"
                       class="saved-cart-orders-error">
                    Couldn't load this cart's details. Try
                    closing and re-expanding the row.
                  </div>
                  <div v-else-if="groupedPurchaseOrders(row.item).length === 0
                                  && cartDetails[String(row.item.id)]
                                  && !cartDetails[String(row.item.id)].loading"
                       class="saved-cart-orders-empty">
                    This cart has no orders to restore.
                  </div>
                  <div v-else>
                    <div v-for="grp in groupedPurchaseOrders(row.item)"
                         :key="'savedord-' + row.item.id + '-' + grp.number"
                         class="saved-cart-order-card">
                      <div class="saved-cart-order-header">
                        <span class="saved-cart-order-label">
                          Order #{{ grp.orderIndex }}
                        </span>
                        <span class="saved-cart-order-meta">
                          PO: <strong>{{ grp.number || '—' }}</strong>
                          ·
                          {{ grp.machineGroups.length }}
                          {{ grp.machineGroups.length === 1
                              ? 'assembly' : 'assemblies' }}
                        </span>
                        <span class="saved-cart-order-cost">
                          {{ formatCurrency(grp.cost) }}
                        </span>
                      </div>

                      <div v-for="(mg, mIdx) in grp.machineGroups"
                           :key="'savedord-mg-' + row.item.id + '-' + grp.number + '-' + mIdx"
                           class="saved-cart-assembly">
                        <div class="saved-cart-assembly-header">
                          <strong class="saved-cart-machine">
                            {{ mg.machineName }}
                          </strong>
                          <span v-if="mg.assemblyName"
                                class="saved-cart-assembly-name">
                            {{ mg.assemblyName }}
                          </span>
                        </div>
                        <table class="saved-cart-parts-table">
                          <thead>
                            <tr>
                              <th class="sc-col-code">Code</th>
                              <th class="sc-col-desc">Description</th>
                              <th class="sc-col-qty">Qty</th>
                              <th class="sc-col-price">Price</th>
                              <th class="sc-col-cost">Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(part, pIdx) in mg.parts"
                                :key="'savedord-pt-' + row.item.id + '-' + grp.number + '-' + mIdx + '-' + pIdx">
                              <td class="sc-col-code">
                                {{ part.code || '—' }}
                                <!-- 2% Discount chip rendered inline
                                     next to the part code. Parent <td>
                                     is a normal table cell so
                                     inline-block + nowrap is enough. -->
                                <span class="saved-cart-discount-chip"
                                      v-if="isWearEligible(part)">
                                  2% Discount
                                </span>
                              </td>
                              <td class="sc-col-desc">{{ part.description || '—' }}</td>
                              <td class="sc-col-qty">{{ part.quantity || 0 }}</td>
                              <td class="sc-col-price">{{ formatCurrency(part.price) }}</td>
                              <td class="sc-col-cost">{{ formatCurrency(part.cost) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </b-table>

          <div class="deleted-carts-section mt-5" v-if="!loading">
            <div class="row align-items-center mb-3">
              <div class="col">
                <h3 class="deleted-carts-heading" @click="toggleDeletedCarts" style="cursor: pointer;">
                  <span class="deleted-carts-arrow" :class="{ open: showDeletedCarts }">&#9662;</span>
                  Recently Deleted Carts
                  <span v-if="deletedCarts.length > 0" class="deleted-carts-count">({{ deletedCarts.length }})</span>
                </h3>
              </div>
            </div>

            <div v-if="showDeletedCarts">
              <div v-if="loadingDeleted" class="mb-3">
                <p>Loading deleted carts...</p>
              </div>
              <div v-else-if="deletedCarts.length === 0" class="mb-3">
                <p style="color: #999;">No deleted carts to restore.</p>
              </div>
              <div v-else>
                <div class="row mb-3">
                  <div class="col">
                    <div class="deleted-carts-toolbar">
  <button class="btn btn-outline-dark btn-sm deleted-action-btn deleted-restore-btn" @click="confirmRestoreAll">
    Restore All
  </button>
  <button class="btn btn-outline-danger btn-sm deleted-action-btn deleted-delete-btn" @click="confirmPermanentDeleteAll">
    Delete All
  </button>
</div>
                  </div>
                </div>

                <b-table
                  id="deletedCartsTable"
                  stacked="lg"
                  :fields="deletedCartFields"
                  :items="deletedCarts"
                  :sort-by.sync="deletedSortBy"
                  :sort-desc.sync="deletedSortDesc"
                  responsive="sm"
                  class="deleted-carts-table"
                >
                  <template v-slot:cell(expand)="row">
                    <button type="button"
                            class="cart-expand-btn"
                            :class="{ 'is-open': row.detailsShowing }"
                            :aria-label="row.detailsShowing ? 'Hide details' : 'Show details'"
                            :title="row.detailsShowing ? 'Hide details' : 'Show details'"
                            @click="row.toggleDetails">
                      <span class="cart-expand-icon">{{ row.detailsShowing ? '−' : '+' }}</span>
                    </button>
                  </template>

                  <template v-slot:cell(dateCreated)="data">
                    {{ data.item.dateCreated | formatDate }}
                  </template>
                  <template v-slot:cell(dateDeleted)="data">
                    {{ data.item.dateDeleted | formatDate }}
                  </template>
                  <template v-slot:cell(type)="data">
                    {{ getCartTypeLabel(data.item) }}
                  </template>
                  <template v-slot:cell(poNumbers)="data">
                    <span v-if="data.item.poNumbers && data.item.poNumbers.length > 0"
                          class="cart-po-list">
                      <span class="cart-po-chip"
                            v-for="(po, idx) in data.item.poNumbers"
                            :key="'dpo-' + data.item.id + '-' + idx + '-' + po">
                        {{ po }}
                      </span>
                    </span>
                    <span v-else class="cart-po-empty">—</span>
                  </template>
                  <template v-slot:cell(itemCount)="data">
                    {{ data.item.itemCount }}
                  </template>
                  <template v-slot:cell(grandTotal)="data">
                    <span class="cart-total-cell">
                      {{ (data.item.grandTotal || 0) | currency }}
                    </span>
                  </template>
                  <template v-slot:cell(actions)="data">
                    <div class="cart-actions">
                      <a href="#" class="cart-restore-link" @click.prevent="restoreCart(data.item)">
                        Restore
                      </a>
                      <a href="#" class="cart-delete-link" @click.prevent="confirmPermanentDelete(data.item)" title="Permanently Delete">
                        &times;
                      </a>
                    </div>
                  </template>

                  <template v-slot:row-details="row">
                    <div class="cart-details-panel">
                      <dl class="cart-details-list">
                        <div class="cart-details-pair">
                          <dt>Company</dt>
                          <dd>{{ row.item.companyName || "—" }}</dd>
                        </div>
                        <div class="cart-details-pair">
                          <dt>Code</dt>
                          <dd>{{ row.item.companyCode || "—" }}</dd>
                        </div>
                      </dl>
                    </div>
                  </template>
                </b-table>
              </div>
            </div>
          </div>
          </div><!-- /Saved tab -->

          <!-- ===== ORDERS TAB =====
               Lightweight summary table of historical orders.
               Same data source as OrderHistory.vue
               (apiService.getOrders) so what users see here
               matches that page. Per-row Review Order action
               mirrors OrderHistory's, plus a "See full
               history" link routes to /account/orderhistory
               for full filtering/paging. -->
          <div v-show="activeTab === 'orders'" v-if="companySelected">
            <div class="tab-pane-header">
              <p class="tab-pane-intro">
                Recently submitted orders.
              </p>
            </div>

            <div class="row" v-if="loadingHistoryOrders">
              <div class="col-12 mt-4"><h3>Loading orders…</h3></div>
            </div>
            <div class="row" v-else-if="historyOrders.length === 0">
              <div class="col-12 mt-4"><h3>No orders found.</h3></div>
            </div>

            <!-- ===== ORDERS FILTER ROW =====
                 Six client-side filters that apply to the
                 already-loaded historyOrders array. Filters
                 substring-match (case-insensitive) on the
                 corresponding field; the date filter matches
                 same-day. For full filtering across all
                 orders (beyond the 25 loaded here), the user
                 clicks "See full Order History" below the
                 table to land on OrderHistory.vue's
                 server-side-paginated page.
                 Hidden when no orders are loaded — there's
                 nothing to filter and the row would push the
                 "No orders found" message off-screen. -->
            <!-- ===== ORDERS FILTER — dropdown-driven =====
                 Pick a filter from "Please select a filter" and only that
                 field appears. Add several; each can be closed (×), which
                 clears its value. Keeps the row uncluttered until needed. -->
            <div class="orders-filter-bar"
                 v-if="!loadingHistoryOrders && historyOrders.length > 0">
              <div class="orders-filter-add" v-if="availableOrderFilterDefs.length > 0">
                <select class="orders-filter-select"
                        v-model="orderFilterPick"
                        @change="onAddOrderFilter">
                  <option value="">Please select a filter…</option>
                  <option v-for="def in availableOrderFilterDefs"
                          :key="def.key"
                          :value="def.key">{{ def.label }}</option>
                </select>
              </div>

              <div class="orders-filter-active" v-if="activeOrderFilters.length > 0">
                <div class="orders-filter-cell"
                     v-for="key in activeOrderFilters"
                     :key="'of-' + key">
                  <label class="orders-filter-label">
                    <span>{{ orderFilterLabel(key) }}</span>
                    <button type="button"
                            class="orders-filter-remove"
                            title="Remove this filter"
                            @click="removeOrderFilter(key)">×</button>
                  </label>
                  <date-picker v-if="key === 'date'"
                               v-model="historyOrdersFilters.date"
                               value-type="date"
                               format="MM/DD/YYYY"
                               placeholder="Date"
                               input-class="orders-filter-input"
                               :clearable="true">
                  </date-picker>
                  <input v-else
                         type="text"
                         class="orders-filter-input"
                         :placeholder="orderFilterLabel(key)"
                         v-model="historyOrdersFilters[key]" />
                </div>
              </div>
            </div>

            <!-- Bulk clear (Orders). Only appears once one or more orders are
                 checked; clears the checked rows (soft-delete to Recently
                 Deleted Orders). -->
            <div class="mb-3" v-if="!loadingHistoryOrders && selectedOrderIds.length > 0">
              <button
                class="btn btn-danger btn-sm compact-action-btn"
                @click="clearHistory('orders')"
                :title="ordersClearButtonTitle"
              >
                Clear All Orders
              </button>
            </div>

            <b-table
              v-if="!loadingHistoryOrders && filteredHistoryOrders.length > 0"
              id="historyOrdersTable"
              stacked="lg"
              :fields="historyOrderFields"
              :items="filteredHistoryOrders"
              responsive="sm"
            >
              <template v-slot:head(select)>
                <input type="checkbox" class="cart-select-all"
                       v-model="allOrdersSelected" :indeterminate.prop="someOrdersSelected"
                       title="Select all shown" />
              </template>
              <template v-slot:cell(select)="data">
                <input type="checkbox" class="cart-select-row"
                       :value="data.item.id" v-model="selectedOrderIds"
                       @click.stop aria-label="Select this order" />
              </template>
              <template v-slot:cell(dateCreated)="data">
                {{ data.item.dateCreated | formatDate }}
              </template>

              <!-- Status chip: green when status contains
                   "complete" (case-insensitive), gray
                   otherwise. Mirrors the dashboard-list-status
                   pattern from Index_-_Account.vue so the
                   visual treatment is consistent across the
                   two surfaces. Falls back to em-dash when the
                   status field is empty. -->
              <template v-slot:cell(status)="data">
                <span class="orders-status-chip"
                      :class="(data.item.status || '').toLowerCase().indexOf('complete') >= 0
                        ? 'is-complete' : 'is-open'">
                  {{ data.item.status || '—' }}
                </span>
              </template>

              <!-- Merged Order # / PO # cell: the order number on
                   top, with its PO number(s) stacked on the lines
                   beneath (an order can span multiple Purchase
                   Orders). poNumbers is derived from each order's
                   purchaseOrders array in loadHistoryOrders. -->
              <template v-slot:cell(id)="data">
                <div class="order-ref">
                  <span class="order-ref-id">{{ data.item.id }}</span>
                  <span class="order-ref-po"
                        v-for="(po, idx) in (data.item.poNumbers || [])"
                        :key="'po-' + data.item.id + '-' + idx">
                    PO: {{ po }}
                  </span>
                </div>
              </template>

              <template v-slot:cell(actions)="data">
                <!-- Review Order link styled like the Saved
                     tab's Resume link (cart-resume-link
                     pattern): brand-red SVG icon + dark gray
                     text, click opens the Order Details page.
                     Eye icon (FA free-solid) is the
                     universally-understood "view/review"
                     symbol — keeps the table visually
                     consistent with the Saved tab actions. -->
                <div class="cart-actions">
                  <a href="#"
                     class="cart-resume-link"
                     @click.prevent="viewHistoryOrder(data.item.id)">
                    <svg class="cart-icon-svg"
                         xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 576 512"
                         width="16"
                         height="16">
                      <path fill="#c0392b"
                        d="M572.5 241.4C518.3 135.6 410.9 64 288 64S57.7 135.6 3.5 241.4a32.4 32.4 0 0 0 0 29.2C57.7 376.4 165.1 448 288 448s230.3-71.6 284.5-177.4a32.4 32.4 0 0 0 0-29.2zM288 400a144 144 0 1 1 144-144 143.9 143.9 0 0 1 -144 144zm0-240a95.3 95.3 0 0 0 -25.3 3.8 47.9 47.9 0 0 1 -66.9 66.9A95.8 95.8 0 1 0 288 160z"/>
                    </svg>
                    Review Order
                  </a>
                  <a href="#"
                     class="cart-delete-link"
                     @click.prevent="confirmDeleteOrder(data.item)"
                     title="Remove from history (restorable)">
                    &times;
                  </a>
                </div>
              </template>
            </b-table>

            <!-- Filters narrowed everything out — distinct from
                 "no orders found" (zero rows from the API).
                 Users see this when their filter values match
                 nothing in the loaded set; the suggestion to
                 click "See full Order History" hints that more
                 results may exist beyond the 25 loaded here. -->
            <div class="row"
                 v-if="!loadingHistoryOrders
                       && historyOrders.length > 0
                       && filteredHistoryOrders.length === 0">
              <div class="col-12 mt-4">
                <p>No orders match your filters. Try clearing one or more, or
                   click <strong>See full Order History</strong> below for the
                   full filterable list.</p>
              </div>
            </div>

            <p class="tab-pane-footer-link"
               v-if="!loadingHistoryOrders && historyOrders.length > 0">
              Showing the most recent 25 orders.
            </p>

            <!-- ===== RECENTLY DELETED ORDERS =====
                 Soft-deleted orders (hidden from the list above) collect
                 here and can be restored. Restore-only — orders are real
                 records and are never hard-deleted. -->
            <div class="deleted-carts-section mt-5"
                 v-if="!loadingHistoryOrders && (currentCompanyId || allCompaniesMode)">
              <div class="row align-items-center mb-3">
                <div class="col">
                  <h3 class="deleted-carts-heading" @click="toggleDeletedOrders" style="cursor: pointer;">
                    <span class="deleted-carts-arrow" :class="{ open: showDeletedOrders }">&#9662;</span>
                    Recently Deleted Orders
                    <span v-if="deletedOrders.length > 0" class="deleted-carts-count">({{ deletedOrders.length }})</span>
                  </h3>
                </div>
              </div>
              <div v-if="showDeletedOrders">
                <div v-if="loadingDeletedOrders" class="mb-3"><p>Loading deleted orders...</p></div>
                <div v-else-if="deletedOrders.length === 0" class="mb-3"><p style="color: #999;">No deleted orders to restore.</p></div>
                <div v-else>
                  <div class="row mb-3">
                    <div class="col">
                      <div class="deleted-carts-toolbar">
                        <button class="btn btn-outline-dark btn-sm deleted-action-btn deleted-restore-btn" @click="confirmRestoreAllOrders">
                          Restore All
                        </button>
                      </div>
                    </div>
                  </div>
                  <b-table
                    id="deletedOrdersTable"
                    stacked="lg"
                    :fields="deletedOrderFields"
                    :items="deletedOrders"
                    responsive="sm"
                    class="deleted-carts-table"
                  >
                    <template v-slot:cell(dateCreated)="data">{{ data.item.dateCreated | formatDate }}</template>
                    <template v-slot:cell(dateDeleted)="data">{{ data.item.dateDeleted | formatDate }}</template>
                    <template v-slot:cell(id)="data">
                      <div class="order-ref">
                        <span class="order-ref-id">{{ data.item.id }}</span>
                        <span class="order-ref-po"
                              v-for="(po, idx) in (data.item.poNumbers || [])"
                              :key="'dopo-' + data.item.id + '-' + idx">PO: {{ po }}</span>
                      </div>
                    </template>
                    <template v-slot:cell(status)="data">
                      <span class="orders-status-chip"
                            :class="(data.item.status || '').toLowerCase().indexOf('complete') >= 0 ? 'is-complete' : 'is-open'">
                        {{ data.item.status || '—' }}
                      </span>
                    </template>
                    <template v-slot:cell(actions)="data">
                      <div class="cart-actions">
                        <a href="#" class="cart-restore-link" @click.prevent="restoreOrder(data.item)">Restore</a>
                      </div>
                    </template>
                  </b-table>
                </div>
              </div>
            </div>
          </div><!-- /Orders tab -->

          <!-- ===== QUOTES TAB =====
               Same pattern. Read endpoint matches what
               RFQHistory.vue uses (httpClient GET /cart with
               cid + paging). Per-row Review and Create Order
               actions mirror RFQHistory's. -->
          <div v-show="activeTab === 'quotes'" v-if="companySelected">
            <div class="tab-pane-header">
              <p class="tab-pane-intro">
                Recently submitted quotes.
              </p>
            </div>

            <div class="row" v-if="loadingHistoryQuotes">
              <div class="col-12 mt-4"><h3>Loading quotes…</h3></div>
            </div>
            <div class="row" v-else-if="historyQuotes.length === 0">
              <div class="col-12 mt-4"><h3>No quotes found.</h3></div>
            </div>

            <!-- Bulk clear bar (Quotes). Same as Orders — clears across the
                 FULL quote history (soft-delete to Recently Deleted Quotes). -->
            <div class="mb-3" v-if="!loadingHistoryQuotes && historyQuotes.length > 0">
              <label class="clear-date-label">Clear quotes before:</label>
              <div class="clear-date-row">
                <date-picker
                  v-model="quotesClearBeforeDate"
                  format="MM/DD/YYYY"
                  placeholder="Select Date"
                  :disabled-date="disabledDate"
                  class="clear-date-picker"
                ></date-picker>
                <button
                  class="btn btn-danger btn-sm ml-2 compact-action-btn"
                  @click="clearHistory('quotes')"
                  :title="quotesClearButtonTitle"
                >
                  {{ quotesClearButtonLabel }}
                </button>
              </div>
            </div>

            <b-table
              v-if="!loadingHistoryQuotes && historyQuotes.length > 0"
              id="historyQuotesTable"
              stacked="lg"
              :fields="historyQuoteFields"
              :items="historyQuotes"
              responsive="sm"
            >
              <template v-slot:head(select)>
                <input type="checkbox" class="cart-select-all"
                       v-model="allQuotesSelected" :indeterminate.prop="someQuotesSelected"
                       title="Select all shown" />
              </template>
              <template v-slot:cell(select)="data">
                <input type="checkbox" class="cart-select-row"
                       :value="data.item.id" v-model="selectedQuoteIds"
                       @click.stop aria-label="Select this quote" />
              </template>
              <template v-slot:cell(dateCreated)="data">
                {{ data.item.dateCreated | formatDate }}
              </template>
              <template v-slot:cell(cost)="data">
                {{ (data.item.cost || 0) | currency }}
              </template>

              <template v-slot:cell(actions)="data">
                <!-- Modern icon+text links matching the Orders
                     tab's "Review Order" action (.cart-resume-link),
                     replacing the old chunky buttons. Review = gray
                     eye link; Create Order = brand-red (the primary
                     action) with a circle-plus icon. -->
                <div class="cart-actions">
                  <a href="#"
                     class="cart-resume-link"
                     @click.prevent="viewHistoryQuote(data.item.id)">
                    <svg class="cart-icon-svg"
                         xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 576 512"
                         width="16" height="16">
                      <path fill="#c0392b"
                        d="M572.5 241.4C518.3 135.6 410.9 64 288 64S57.7 135.6 3.5 241.4a32.4 32.4 0 0 0 0 29.2C57.7 376.4 165.1 448 288 448s230.3-71.6 284.5-177.4a32.4 32.4 0 0 0 0-29.2zM288 400a144 144 0 1 1 144-144 143.9 143.9 0 0 1 -144 144zm0-240a95.3 95.3 0 0 0 -25.3 3.8 47.9 47.9 0 0 1 -66.9 66.9A95.8 95.8 0 1 0 288 160z"/>
                    </svg>
                    Review
                  </a>
                  <a href="#"
                     class="cart-resume-link quote-create-link"
                     @click.prevent="createOrderFromQuote(data.item.id)">
                    <svg class="cart-icon-svg"
                         xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 512 512"
                         width="16" height="16">
                      <path fill="#c0392b"
                        d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 344l0-64-64 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l64 0 0-64c0-13.3 10.7-24 24-24s24 10.7 24 24l0 64 64 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-64 0 0 64c0 13.3-10.7 24-24 24s-24-10.7-24-24z"/>
                    </svg>
                    Create Order
                  </a>
                  <a href="#"
                     class="cart-delete-link"
                     @click.prevent="confirmDeleteQuote(data.item)"
                     title="Remove from history (restorable)">
                    &times;
                  </a>
                </div>
              </template>
            </b-table>

            <p class="tab-pane-footer-link"
               v-if="!loadingHistoryQuotes && historyQuotes.length > 0">
              Showing the most recent 25 quotes.
            </p>

            <!-- ===== RECENTLY DELETED QUOTES =====
                 Soft-deleted quotes (carts, IsSubmitted+Rfq) collect here
                 and can be restored. Mirrors the Recently Deleted Carts
                 table; restore-only. -->
            <div class="deleted-carts-section mt-5"
                 v-if="!loadingHistoryQuotes && (currentCompanyId || allCompaniesMode)">
              <div class="row align-items-center mb-3">
                <div class="col">
                  <h3 class="deleted-carts-heading" @click="toggleDeletedQuotes" style="cursor: pointer;">
                    <span class="deleted-carts-arrow" :class="{ open: showDeletedQuotes }">&#9662;</span>
                    Recently Deleted Quotes
                    <span v-if="deletedQuotes.length > 0" class="deleted-carts-count">({{ deletedQuotes.length }})</span>
                  </h3>
                </div>
              </div>
              <div v-if="showDeletedQuotes">
                <div v-if="loadingDeletedQuotes" class="mb-3"><p>Loading deleted quotes...</p></div>
                <div v-else-if="deletedQuotes.length === 0" class="mb-3"><p style="color: #999;">No deleted quotes to restore.</p></div>
                <div v-else>
                  <div class="row mb-3">
                    <div class="col">
                      <div class="deleted-carts-toolbar">
                        <button class="btn btn-outline-dark btn-sm deleted-action-btn deleted-restore-btn" @click="confirmRestoreAllQuotes">
                          Restore All
                        </button>
                      </div>
                    </div>
                  </div>
                  <b-table
                    id="deletedQuotesTable"
                    stacked="lg"
                    :fields="deletedCartFields"
                    :items="deletedQuotes"
                    :sort-by.sync="deletedSortBy"
                    :sort-desc.sync="deletedSortDesc"
                    responsive="sm"
                    class="deleted-carts-table"
                  >
                    <template v-slot:cell(expand)="row">
                      <button type="button" class="cart-expand-btn" :class="{ 'is-open': row.detailsShowing }"
                              :aria-label="row.detailsShowing ? 'Hide details' : 'Show details'"
                              @click="row.toggleDetails">
                        <span class="cart-expand-icon">{{ row.detailsShowing ? '−' : '+' }}</span>
                      </button>
                    </template>
                    <template v-slot:cell(dateCreated)="data">{{ data.item.dateCreated | formatDate }}</template>
                    <template v-slot:cell(dateDeleted)="data">{{ data.item.dateDeleted | formatDate }}</template>
                    <template v-slot:cell(type)="data">{{ getCartTypeLabel(data.item) }}</template>
                    <template v-slot:cell(poNumbers)="data">
                      <span v-if="data.item.poNumbers && data.item.poNumbers.length > 0" class="cart-po-list">
                        <span class="cart-po-chip"
                              v-for="(po, idx) in data.item.poNumbers"
                              :key="'dqpo-' + data.item.id + '-' + idx + '-' + po">{{ po }}</span>
                      </span>
                      <span v-else class="cart-po-empty">—</span>
                    </template>
                    <template v-slot:cell(itemCount)="data">{{ data.item.itemCount }}</template>
                    <template v-slot:cell(grandTotal)="data">
                      <span class="cart-total-cell">{{ (data.item.grandTotal || 0) | currency }}</span>
                    </template>
                    <template v-slot:cell(actions)="data">
                      <div class="cart-actions">
                        <a href="#" class="cart-restore-link" @click.prevent="restoreQuote(data.item)">Restore</a>
                      </div>
                    </template>
                    <template v-slot:row-details="row">
                      <div class="cart-details-panel">
                        <dl class="cart-details-list">
                          <div class="cart-details-pair"><dt>Company</dt><dd>{{ row.item.companyName || "—" }}</dd></div>
                          <div class="cart-details-pair"><dt>Code</dt><dd>{{ row.item.companyCode || "—" }}</dd></div>
                        </dl>
                      </div>
                    </template>
                  </b-table>
                </div>
              </div>
            </div>
          </div><!-- /Quotes tab -->
        </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDeletingOverlay" class="deleting-overlay">
      <div class="deleting-message">
        <div class="deleting-spinner"></div>
        <p>{{ deletingMessage }}</p>
      </div>
    </div>

    <!-- ===== RESUME CONFLICT MODAL =====
         Opens when the user clicks Resume on a saved cart
         while a different cart of the same type is already
         loaded as their active cart. Three actions:

         - Replace: swap the active-cart pointer to this saved
           cart. The previously-active cart isn't deleted; it
           goes back to showing as another row in this list,
           with its Resume button restored. Reversible.

         - Merge: combine the saved cart's purchaseOrders into
           the active cart (creates a new cart with both sets
           of orders, deletes the two originals). Useful when
           the user wants to ship from one cart but the parts
           are split across two saved sessions.

         - Cancel: close the modal without changing anything.

         Custom action row in body (hide-footer) instead of
         b-modal default footer — matches the pattern used
         elsewhere in this app for predictable button layout
         and consistent styling. -->
    <b-modal id="resume-choice-modal"
             v-model="showResumeChoiceModal"
             title="A Cart Is Already Loaded"
             size="md"
             centered
             hide-footer
             modal-class="resume-choice-modal"
             @hide="cancelResumeChoice">

      <p class="resume-choice-intro">
        You already have an active cart loaded. What would
        you like to do with the cart you just clicked Resume
        on?
      </p>

      <div class="resume-choice-options">
        <!-- REPLACE option. Reads "Replace" rather than
             "Swap" because users think of it as swapping the
             button-circled icon, not the technical pointer. -->
        <button type="button"
                class="btn btn-outline-dark resume-choice-btn"
                :disabled="mergingResume"
                @click="confirmSwap">
          <strong>Replace Active Cart</strong>
          <span class="resume-choice-hint">
            Set this cart as your active cart. Your previous
            active cart goes back to Saved Carts unchanged.
          </span>
        </button>

        <!-- MERGE option. Disabled briefly while the merge
             API orchestration runs (button label flips to
             "Merging…" so the user has feedback). -->
        <button type="button"
                class="btn btn-primary resume-choice-btn"
                :disabled="mergingResume"
                @click="confirmMerge">
          <strong>{{ mergingResume ? 'Merging…' : 'Merge Into Active Cart' }}</strong>
          <span class="resume-choice-hint">
            Combine both carts' orders into one cart so you
            can review and place them together.
          </span>
        </button>
      </div>

      <div class="resume-choice-cancel-row">
        <button type="button"
                class="btn btn-link resume-choice-cancel"
                :disabled="mergingResume"
                @click="cancelResumeChoice">
          Cancel
        </button>
      </div>
    </b-modal>
  </section>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
import httpClient from "@/assets/js/HttpClient";
const apiService = new FeApiService();

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import SelectCompanyPrompt from "@/components/Common/SelectCompanyPrompt.vue";
import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
  name: "SavedCarts",
  components: { DatePicker, SelectCompanyPrompt },
  computed: {
    // True when there's a company scope to show data for: either a
    // specific company is selected, or the admin "All Companies" mode is
    // on. Mirrors the existing `!currentCompanyId && !allCompaniesMode`
    // guard the per-tab empty messages used. currentCompanyId is kept in
    // sync by the existing companyCheckInterval poll, so this re-evaluates
    // reactively on company change.
    companySelected() {
      return !!this.currentCompanyId || this.allCompaniesMode;
    },
    // ===== Bulk-clear selection (Saved tab) =====
    // Header select-all. get(): true only when every visible cart is
    // checked. set(): check all / none.
    allCartsSelected: {
      get() {
        return !!(this.carts && this.carts.length > 0
          && this.selectedCartIds.length === this.carts.length);
      },
      set(val) {
        this.selectedCartIds = val
          ? this.carts.map(function (c) { return c.id; })
          : [];
      }
    },
    // Drives the header checkbox's indeterminate dash (some, not all).
    someCartsSelected() {
      var total = this.carts ? this.carts.length : 0;
      return this.selectedCartIds.length > 0 && this.selectedCartIds.length < total;
    },
    // The single Clear button is context-aware. Priority:
    //   1) checked rows  2) "clear before" date  3) clear all.
    clearButtonLabel() {
      if (this.selectedCartIds.length > 0) {
        return "Clear Selected (" + this.selectedCartIds.length + ")";
      }
      if (this.clearBeforeDate) {
        return "Clear Before Date";
      }
      return "Clear All";
    },
    clearButtonTitle() {
      if (this.selectedCartIds.length > 0) {
        return "Delete the " + this.selectedCartIds.length + " checked cart(s)";
      }
      if (this.clearBeforeDate) {
        return "Delete carts created on or before the selected date";
      }
      return "Delete all saved carts (you'll be asked to confirm)";
    },

    // Defensive master/admin detection. Role information may arrive in
    // several shapes depending on the backend version, so we check all of
    // them: explicit flags, a single role string, and a roles array that
    // may contain strings or {name} objects. Matches case-insensitively
    // against "master" or "admin".
    currentUser() {
      if (this.$store && this.$store.state) {
        return (
          this.$store.state.currentUser ||
          this.$store.state.user ||
          (this.$store.getters && this.$store.getters.currentUser) ||
          null
        );
      }
      return null;
    },
    // Mirrors Index_-_Account.vue's isMasterUser — checks the
    // currentUser object across all the shapes role data
    // arrives in (flags, single string, roles array of
    // strings or {name} objects). Used to gate the
    // "All Companies" toggle in the tab strip; non-admins
    // never see it.
    isMasterUser() {
      const u = this.currentUser || {};
      if (u.isAdmin === true || u.isMaster === true) return true;
      const matchesRole = function (val) {
        if (!val) return false;
        const s =
          typeof val === "string"
            ? val
            : (val && val.name) || (val && val.role) || "";
        const low = String(s).toLowerCase();
        return low === "master" || low === "admin";
      };
      if (matchesRole(u.role)) return true;
      if (Array.isArray(u.roles) && u.roles.some(matchesRole)) return true;
      return false;
    },
    // ===== HISTORY TABLE FIELD DEFINITIONS =====
    // Computed (not static data) so the "Company" column is
    // included ONLY in all-companies mode — when the user is
    // viewing a single company it's redundant. The Order # / PO #
    // column is merged: the cell(id) slot stacks the PO number(s)
    // beneath the order number.
    historyOrderFields() {
      var f = [
        { key: "select", label: "", thClass: "cart-select-th", tdClass: "cart-select-td" },
        { key: "dateCreated", label: "Date" },
        { key: "id", label: "Order # / PO #" },
      ];
      if (this.allCompaniesMode) {
        f.push({ key: "company.code", label: "Company" });
      }
      f.push(
        { key: "cost", label: "Total", formatter: function (v) {
            if (v == null) return "$0.00";
            return "$" + Number(v).toFixed(2);
          } },
        { key: "status", label: "Status" },
        { key: "actions", label: "" }
      );
      return f;
    },
    historyQuoteFields() {
      var f = [
        { key: "select", label: "", thClass: "cart-select-th", tdClass: "cart-select-td" },
        { key: "dateCreated", label: "Date" },
        { key: "id", label: "Quote #" },
      ];
      if (this.allCompaniesMode) {
        f.push({ key: "company.code", label: "Company" });
      }
      f.push(
        { key: "cost", label: "Total" },
        { key: "actions", label: "" }
      );
      return f;
    },
    // Columns for the Recently Deleted Orders table — order-history shape
    // plus a Date Deleted column. Mirrors historyOrderFields.
    deletedOrderFields() {
      var f = [
        { key: "dateCreated", label: "Date Created" },
        { key: "dateDeleted", label: "Date Deleted" },
        { key: "id", label: "Order # / PO #" }
      ];
      if (this.allCompaniesMode) {
        f.push({ key: "company.code", label: "Company" });
      }
      f.push(
        { key: "cost", label: "Total", formatter: function (v) {
            if (v == null) return "$0.00";
            return "$" + Number(v).toFixed(2);
          } },
        { key: "status", label: "Status" },
        { key: "actions", label: "" }
      );
      return f;
    },
    // ===== Bulk-clear selection state (Orders / Quotes tabs) =====
    // Select-all toggles selection over the rows currently shown.
    allOrdersSelected: {
      get() {
        var rows = this.filteredHistoryOrders || [];
        return rows.length > 0 && this.selectedOrderIds.length === rows.length;
      },
      set(val) {
        this.selectedOrderIds = val
          ? (this.filteredHistoryOrders || []).map(function (o) { return o.id; })
          : [];
      }
    },
    someOrdersSelected() {
      var total = (this.filteredHistoryOrders || []).length;
      return this.selectedOrderIds.length > 0 && this.selectedOrderIds.length < total;
    },
    ordersClearButtonLabel() {
      if (this.selectedOrderIds.length > 0) return "Clear Selected (" + this.selectedOrderIds.length + ")";
      if (this.ordersClearBeforeDate) return "Clear Before Date";
      return "Clear All";
    },
    ordersClearButtonTitle() {
      if (this.selectedOrderIds.length > 0) return "Move the " + this.selectedOrderIds.length + " checked order(s) to Recently Deleted Orders";
      if (this.ordersClearBeforeDate) return "Move all orders created on or before the date to Recently Deleted Orders (full history)";
      return "Move ALL orders to Recently Deleted Orders (full history)";
    },
    allQuotesSelected: {
      get() {
        var rows = this.historyQuotes || [];
        return rows.length > 0 && this.selectedQuoteIds.length === rows.length;
      },
      set(val) {
        this.selectedQuoteIds = val
          ? (this.historyQuotes || []).map(function (q) { return q.id; })
          : [];
      }
    },
    someQuotesSelected() {
      var total = (this.historyQuotes || []).length;
      return this.selectedQuoteIds.length > 0 && this.selectedQuoteIds.length < total;
    },
    quotesClearButtonLabel() {
      if (this.selectedQuoteIds.length > 0) return "Clear Selected (" + this.selectedQuoteIds.length + ")";
      if (this.quotesClearBeforeDate) return "Clear Before Date";
      return "Clear All";
    },
    quotesClearButtonTitle() {
      if (this.selectedQuoteIds.length > 0) return "Move the " + this.selectedQuoteIds.length + " checked quote(s) to Recently Deleted Quotes";
      if (this.quotesClearBeforeDate) return "Move all quotes created on or before the date to Recently Deleted Quotes (full history)";
      return "Move ALL quotes to Recently Deleted Quotes (full history)";
    },
    // Applies the historyOrdersFilters to historyOrders for
    // the Orders tab b-table. Empty filter fields are skipped
    // (so leaving everything blank shows all 25 loaded rows).
    // Substring-match is case-insensitive across the string
    // fields; date filter matches by calendar day (so a date
    // picker selection of "05/05/2026" matches any order
    // whose dateCreated falls on that calendar day, ignoring
    // time of day). Order Total filter matches when the
    // numeric value's string form contains the filter — e.g.
    // "5228" matches $5,228.10. PO Number searches across
    // all POs on an order (poNumbers can be an array).
    // The set of filters a user can add on the Orders tab, in display order.
    orderFilterDefs() {
      return [
        { key: "date", label: "Date" },
        { key: "orderId", label: "Order #" },
        { key: "companyCode", label: "Company Code" },
        { key: "total", label: "Order Total" },
        { key: "status", label: "Status" },
        { key: "poNumber", label: "PO Number" },
      ];
    },
    // Filters not yet shown — drive the "Please select a filter" dropdown.
    availableOrderFilterDefs() {
      var active = this.activeOrderFilters || [];
      return this.orderFilterDefs.filter(function (d) {
        return active.indexOf(d.key) < 0;
      });
    },
    filteredHistoryOrders() {
      var f = this.historyOrdersFilters || {};
      var hasAny =
        f.date ||
        (f.orderId || "").trim() ||
        (f.companyCode || "").trim() ||
        (f.total || "").trim() ||
        (f.status || "").trim() ||
        (f.poNumber || "").trim();
      if (!hasAny) return this.historyOrders;

      var orderIdQ = (f.orderId || "").trim().toLowerCase();
      var companyQ = (f.companyCode || "").trim().toLowerCase();
      var totalQ = (f.total || "").trim().toLowerCase();
      var statusQ = (f.status || "").trim().toLowerCase();
      var poQ = (f.poNumber || "").trim().toLowerCase();
      var dateQ = f.date ? new Date(f.date) : null;

      function sameDay(a, b) {
        if (!a || !b) return false;
        var ad = new Date(a);
        var bd = new Date(b);
        return ad.getFullYear() === bd.getFullYear()
            && ad.getMonth() === bd.getMonth()
            && ad.getDate() === bd.getDate();
      }

      var rows = this.historyOrders.filter(function (o) {
        if (orderIdQ) {
          var idStr = String(o.id || "").toLowerCase();
          if (idStr.indexOf(orderIdQ) < 0) return false;
        }
        if (companyQ) {
          var code = (o.company && o.company.code) || o.companyCode || "";
          if (String(code).toLowerCase().indexOf(companyQ) < 0) return false;
        }
        if (totalQ) {
          var costStr = String(o.cost == null ? "" : o.cost).toLowerCase();
          if (costStr.indexOf(totalQ) < 0) return false;
        }
        if (statusQ) {
          var statusStr = String(o.status || "").toLowerCase();
          if (statusStr.indexOf(statusQ) < 0) return false;
        }
        if (poQ) {
          var pos = o.poNumbers;
          var posStr;
          if (Array.isArray(pos)) {
            posStr = pos.join(" ");
          } else {
            posStr = String(pos || "");
          }
          if (posStr.toLowerCase().indexOf(poQ) < 0) return false;
        }
        if (dateQ) {
          if (!sameDay(o.dateCreated, dateQ)) return false;
        }
        return true;
      });

      // ===== PRIORITY STATUS SORT =====
      // Tier 0 = "Review" (waiting on the user)
      // Tier 1 = any of "Prepared to Ship" / "Preparing to
      //          Ship" / "Prepare to Ship" — actively being
      //          worked, the user may have shipping questions
      // Tier 2 = everything else (Complete, etc.)
      // Within a tier, ties break by dateCreated desc so
      // newest sits first. All comparisons are lower-cased
      // so backend casing variations match. The shipping
      // tier accepts three phrasings because the actual
      // backend status string isn't documented in the front-
      // end — adding all three covers continuous, past, and
      // imperative tense without needing to peek at the
      // backend. Extend the set if the user adds new
      // priority statuses (e.g. "Awaiting Approval").
      var SHIP_VARIANTS = {
        "prepared to ship": true,
        "preparing to ship": true,
        "prepare to ship": true,
      };
      function tier(o) {
        var s = String((o && o.status) || "").toLowerCase().trim();
        if (s === "review") return 0;
        if (SHIP_VARIANTS[s]) return 1;
        return 2;
      }
      function dateMs(o) {
        if (!o || !o.dateCreated) return 0;
        var d = new Date(o.dateCreated);
        var t = d.getTime();
        return isNaN(t) ? 0 : t;
      }
      // .slice() to avoid mutating the filter result (which
      // could ripple into Vue reactivity surprises if anyone
      // referenced the array elsewhere).
      return rows.slice().sort(function (a, b) {
        var ta = tier(a);
        var tb = tier(b);
        if (ta !== tb) return ta - tb;
        return dateMs(b) - dateMs(a);
      });
    },
  },
  mounted() {
    // Seed currentCompanyId from whichever source has it (localStorage or
    // Vuex). If Vuex had it but localStorage didn't, mirror so the rest
    // of the app picks it up.
    var resolved = this.resolveCompanyId();
    this.currentCompanyId = resolved;
    if (resolved && !localStorage.companyId) {
      localStorage.companyId = resolved;
    }

    // Sync activeTab from ?tab=<name> on initial load so
    // direct links and refreshes respect the URL. Falls back
    // to 'saved' when the param is missing or unknown.
    var initialTab = this.$route.query.tab;
    if (initialTab === "orders" || initialTab === "quotes") {
      // Use setTab so the lazy-load fetches fire too —
      // direct linking to the Orders or Quotes tab should
      // populate that tab's data, not just flip the visible
      // panel.
      this.setTab(initialTab);
    }

    this.loadCarts();
    // Fire-and-forget — the chip is a pure UI hint, so any
    // failure to load just means no chips render anywhere.
    // No user-visible error path needed.
    this.loadWearPartIds();

    // ===== FLASH-CONFIRM SETUP =====
    // /cart's Save flow lands the user here with
    // ?flashCartId=<id> when a cart is saved (all-selected
    // path) or partial-saved (a fresh saved cart row was
    // created). Capture the id, then schedule it to clear
    // after the animation runs so re-mounts don't flash
    // forever and accidental refreshes (which preserve the
    // query string) don't spam the highlight.
    //
    // The "flash" itself is a CSS animation triggered by a
    // class on the <tr> via tbody-tr-class — see
    // savedTrClass() for the binding. Animation duration
    // (~2.4s) matches the timeout below, plus a buffer so
    // the class stays applied through the full keyframes.
    //
    // We also strip the query param so a back-navigation
    // and forward-again doesn't re-trigger.
    var flashId = this.$route.query.flashCartId;
    if (flashId) {
      var parsed = parseInt(flashId, 10);
      if (!isNaN(parsed)) {
        this.flashedCartId = parsed;
        var self = this;
        setTimeout(function () {
          self.flashedCartId = null;
        }, 3000);
        // Strip the query param to keep the URL clean —
        // catch swallows the NavigationDuplicated edge case.
        var preserved = this.$route.query.tab
          ? { tab: this.$route.query.tab }
          : {};
        this.$router
          .replace({ path: this.$route.path, query: preserved })
          .catch(function () {});
      }
    }

    // Eagerly load the Orders and Quotes counts on mount so
    // the tab badges show totals immediately, regardless of
    // which tab the user is viewing. Previously these only
    // loaded on first click of the corresponding tab (lazy-
    // load), which meant the badges were always missing on
    // initial page render — users had no way to see "I have
    // 12 orders in my history" without first clicking Orders.
    //
    // Each loader sets a "have I started" flag that prevents
    // setTab from re-firing the same fetch on subsequent
    // clicks. The setTab logic (further down) already gates
    // on historyOrders.length / historyQuotes.length being
    // zero, so a successful eager load makes that gate skip
    // the redundant fetch on click.
    if (this.activeTab !== "orders") {
      this.loadHistoryOrders();
    }
    if (this.activeTab !== "quotes") {
      this.loadHistoryQuotes();
    }
    if (this.$cartBus) {
      this.$cartBus.$on("cart-updated", this.loadCarts);
    }
    this.companyCheckInterval = setInterval(function () {
      var newCompanyId = this.resolveCompanyId();
      if (newCompanyId !== this.currentCompanyId) {
        this.currentCompanyId = newCompanyId;
        if (newCompanyId && !localStorage.companyId) {
          localStorage.companyId = newCompanyId;
        }
        this.loadCarts();
        if (this.showDeletedCarts) {
          this.loadDeletedCarts();
        }
        // Reload the company-scoped wear list that drives the
        // "2% Discount" chip so it reflects the new company's
        // own wear parts.
        this.loadWearPartIds();
      }
    }.bind(this), 500);
  },
  beforeDestroy() {
    if (this.$cartBus) {
      this.$cartBus.$off("cart-updated", this.loadCarts);
    }
    if (this.companyCheckInterval) {
      clearInterval(this.companyCheckInterval);
    }
  },
  watch: {
    // Refetch all loaded tab data when the admin flips the
    // All Companies toggle. loadCarts always runs (it
    // populates the Saved tab badge count too). Orders and
    // Quotes have lazy caches — clear them so a tab switch
    // refetches with the new mode, and refetch immediately
    // if the user is currently viewing one of those tabs.
    // Persist the new value to localStorage so subsequent
    // visits remember it (see data() restore block).
    allCompaniesMode(newVal) {
      try {
        localStorage.savedCartsAllCompanies = newVal ? "1" : "";
      } catch (e) { /* localStorage unavailable */ }
      this.loadCarts();
      this.historyOrders = [];
      this.historyOrdersCount = 0;
      this.historyQuotes = [];
      this.historyQuotesCount = 0;
      if (this.activeTab === "orders") {
        this.loadHistoryOrders();
      } else if (this.activeTab === "quotes") {
        this.loadHistoryQuotes();
      }
    },
    // Persist the Orders tab filters as the user types /
    // changes the date. Deep watcher so any field's mutation
    // triggers the save. The filters apply CLIENT-SIDE so
    // there's no server round trip on each keystroke — the
    // localStorage write is the only side effect, fast and
    // synchronous. Date objects serialize as ISO strings via
    // JSON.stringify; data() restoration re-hydrates them.
    historyOrdersFilters: {
      deep: true,
      handler: function (newVal) {
        try {
          localStorage.savedCartsOrdersFilters = JSON.stringify(newVal || {});
        } catch (e) { /* localStorage unavailable / quota */ }
      },
    },
    // Browser back/forward navigation between tab states
    // (or any external route change that swaps ?tab=) should
    // flip the visible tab. Without this, the URL would say
    // ?tab=quotes but the page would still show Orders if
    // the user pressed Back.
    "$route.query.tab": function (newTab) {
      var resolved = (newTab === "orders" || newTab === "quotes")
        ? newTab
        : "saved";
      if (resolved !== this.activeTab) {
        // Same lazy-load semantics as a click — flip the
        // panel and trigger fetches if the data isn't there.
        this.activeTab = resolved;
        if (resolved === "orders" && this.historyOrders.length === 0
            && !this.loadingHistoryOrders) {
          this.loadHistoryOrders();
        }
        if (resolved === "quotes" && this.historyQuotes.length === 0
            && !this.loadingHistoryQuotes) {
          this.loadHistoryQuotes();
        }
      }
    },
  },
  data() {
    // ===== STATE RESTORATION FROM LOCALSTORAGE =====
    // Restore the admin all-companies toggle and the Orders
    // tab filters when the component mounts so navigating
    // away and back preserves the user's view. Done HERE in
    // data() — not mounted() — for two reasons:
    //   1) The values are visible the moment the template
    //      first renders (no flash of "default state then
    //      restored state").
    //   2) Watchers don't fire on initial data assignment
    //      (only on subsequent mutations), so loadCarts()
    //      sees the right value on its first call without
    //      a wasted re-fetch from a watcher chain.
    // try/catch around localStorage in case it's disabled
    // (private mode in some browsers, etc.) — silent failure
    // means the user sees default state, not a crash.
    var restoredAdmin = false;
    try {
      restoredAdmin = localStorage.savedCartsAllCompanies === "1";
    } catch (e) { /* localStorage unavailable */ }

    // Account scope (customer-only "All account orders" vs "Just mine").
    // Shares the dashboard's key so the choice is consistent across pages.
    // Defaults to the whole account so the lists aren't empty for users whose
    // teammates place the orders.
    var restoredScope = "account";
    try {
      if (localStorage.getItem("jcsteele.dashboard.accountScope") === "mine") {
        restoredScope = "mine";
      }
    } catch (e) { /* localStorage unavailable */ }

    var restoredFilters = {
      date: null,
      orderId: "",
      companyCode: "",
      total: "",
      status: "",
      poNumber: "",
    };
    try {
      var rawFilters = localStorage.savedCartsOrdersFilters;
      if (rawFilters) {
        var parsed = JSON.parse(rawFilters);
        if (parsed && typeof parsed === "object") {
          // vue2-datepicker stores Date objects;
          // JSON.stringify serializes Date to ISO string,
          // so on restore we re-hydrate to a Date instance.
          // Guard against an invalid date string by checking
          // getTime() — fall back to null if malformed.
          if (parsed.date) {
            var d = new Date(parsed.date);
            parsed.date = isNaN(d.getTime()) ? null : d;
          }
          restoredFilters = Object.assign(restoredFilters, parsed);
        }
      }
    } catch (e) { /* malformed JSON or localStorage unavailable */ }

    return {
      // Mirrors what the standalone CartWidget reports via its
      // inline-visibility-change event. Drives the col-lg-9 /
      // col-12 swap on the tabs column so the tabbed content can
      // fill the row when the cart side is empty or collapsed.
      loading: true,
      carts: [],
      // ===== ACTIVE CART TRACKING =====
      // Tracks which saved-cart row(s) are currently loaded
      // as the user's active cart so we can render a disabled
      // "In Cart" indicator instead of the Resume action on
      // those rows. Two slots because order-type and quote-
      // type carts use separate localStorage pointers
      // (cartId vs cartIdRFQ) — a user can have one of each
      // active simultaneously. Refreshed on mount and after
      // every loadCarts so it stays in sync with reality.
      // localStorage isn't reactive on its own; copying into
      // component state and refreshing alongside the cart
      // list is the cheapest way to make the template react.
      activeCartId: null,
      activeCartIdRFQ: null,
      // ===== EXPANDED-ROW DETAIL CACHE =====
      // Map keyed by cart.id holding the full purchaseOrders
      // payload (machine + assembly + parts) shown in the
      // row-details panel. The cart list endpoint
      // (getActiveCarts) returns summary fields only — full
      // breakdown requires a per-cart fetch via getCart. We
      // do that lazily on first expand and cache the result
      // so re-expanding the same row is instant.
      // {[cartId]: { loading: bool, error: bool, data: {} }}
      cartDetails: {},

      // ===== FLASH-CONFIRM HIGHLIGHT =====
      // Holds the id of a cart that should briefly pulse
      // when the user lands on this page. Set from the
      // ?flashCartId=<id> query param (passed by /cart's
      // Save flow), cleared after the animation completes
      // so the highlight only fires once per landing.
      // Wires through to the Saved tab's b-table via
      // tbody-tr-class — see savedTrClass().
      flashedCartId: null,

      // ===== RESUME CONFLICT MODAL =====
      // When the user clicks Resume on a saved cart while
      // a DIFFERENT cart of the same type is already loaded
      // as their active cart, we open this modal to ask what
      // they want to do — replace the active cart, merge the
      // two together, or cancel.
      //
      // pendingResumeCart holds the saved-cart object the
      // user clicked Resume on, queued while the modal is
      // open. confirmSwap / confirmMerge read from it on
      // their respective button clicks.
      //
      // mergingResume is set during the merge API
      // orchestration so the modal buttons can disable
      // themselves and show "Merging…" feedback.
      showResumeChoiceModal: false,
      pendingResumeCart: null,
      mergingResume: false,
      // ===== TABS =====
      // Active tab — drives which content panel is visible.
      // 'saved' | 'orders' | 'quotes'. Defaults to 'saved'
      // since that's the most common entry point.
      activeTab: "saved",
      // ===== WEAR PARTS MAP =====
      // Object map keyed by partId (string|number). Loaded on
      // mount via apiService.getWearPartIds for the user's
      // currently-resolved company. Drives the "2% Discount"
      // chip rendered next to wear-eligible part codes in the
      // expanded parts table on the Saved tab. Same pattern
      // used in Index_-_Cart.vue and RFQSavedItems.vue.
      //
      // Admin-mode caveat: when allCompaniesMode is on, this
      // map still reflects the admin's selected company —
      // chip accuracy is best when viewing the admin's own
      // company's rows. Wear eligibility is a per-company
      // concept so a true admin solution would lazy-load
      // per-row wear maps. Out of scope here; the backend
      // applies actual discount pricing regardless of what
      // the chip shows.
      wearPartIds: {},
      // ===== ADMIN: ALL-COMPANIES MODE =====
      // When true, the three tab loaders (loadCarts,
      // loadHistoryOrders, loadHistoryQuotes) send empty
      // companyId so the backend returns rows across all
      // companies the admin can see. Default false. Gated
      // UI-side via isMasterUser — non-admins can't flip
      // this (and even if they did via devtools, backend
      // role-based filtering still applies). Restored from
      // localStorage at component init; see data() prep
      // block above.
      allCompaniesMode: restoredAdmin,
      // ===== CUSTOMER: ACCOUNT SCOPE =====
      // "account" → everyone on the selected company; "mine" → only this
      // user's own orders/quotes/carts. Gated UI-side via !isMasterUser
      // (admins use the All-Companies toggle instead). Passed as accountWide
      // to the three tab loaders; the backend only widens for verified
      // members of the company, so it can't leak another account's data.
      accountScope: restoredScope,
      // ===== ORDERS HISTORY (Orders tab) =====
      // Same data shape OrderHistory.vue uses (apiService
      // .getOrders pagination response). We grab page 1 with
      // a reasonable per_page so the tab gives a recent
      // overview; full filtering/paging stays on the
      // dedicated OrderHistory page.
      historyOrders: [],
      historyOrdersCount: 0,
      loadingHistoryOrders: false,
      // ===== ORDERS TAB CLIENT-SIDE FILTERS =====
      // Applied via filteredHistoryOrders computed against
      // the already-loaded historyOrders array. Six fields
      // matching the filter row markup. All are strings
      // except `date` which is a Date (vue2-datepicker
      // returns Date objects with value-type="date").
      // Filtering is substring-match, case-insensitive,
      // except date which matches same-day. Restored from
      // localStorage at component init; see data() prep
      // block above.
      historyOrdersFilters: restoredFilters,
      // ===== QUOTES HISTORY (Quotes tab) =====
      // Read endpoint matches RFQHistory.vue's pattern —
      // httpClient GET /cart with paging + cid params,
      // returning the same {data: [[...]], total, current_page,
      // per_page} shape.
      historyQuotes: [],
      historyQuotesCount: 0,
      loadingHistoryQuotes: false,
      // historyOrderFields + historyQuoteFields now live in the
      // computed block so the Company column can be gated on
      // all-companies mode (and the Order #/PO # column merged).
      currentCompanyId: "",
      companyCheckInterval: null,
      cartFields: [
        // Layer 1 (always visible). Secondary fields (Company /
        // Code / Owner) live in the row-details slot — see the
        // template's <template v-slot:row-details> on this table.
        { key: "select", label: "", thClass: "cart-select-th", tdClass: "cart-select-td" },
        { key: "expand", label: "", thClass: "cart-expand-th", tdClass: "cart-expand-td" },
        { key: "dateCreated", label: "Date Created", sortable: true },
        { key: "type", label: "Type" },
        { key: "poNumbers", label: "PO / Quote #" },
        { key: "itemCount", label: "Items", sortable: true },
        { key: "grandTotal", label: "Grand Total", sortable: true, thClass: "cart-total-th", tdClass: "cart-total-td" },
        { key: "actions", label: "" }
      ],
      sortBy: "dateCreated",
      sortDesc: true,
      clearBeforeDate: null,
      selectedCartIds: [],
      showDeletingOverlay: false,
      deletingMessage: "Clearing saved carts...",
      showDeletedCarts: false,
      loadingDeleted: false,
      deletedCarts: [],
      deletedCartFields: [
        // Layer 1 — keeps Date Deleted (specific to this view).
        // Secondary fields live in the row-details slot.
        { key: "expand", label: "", thClass: "cart-expand-th", tdClass: "cart-expand-td" },
        { key: "dateCreated", label: "Date Created", sortable: true },
        { key: "dateDeleted", label: "Date Deleted", sortable: true },
        { key: "type", label: "Type" },
        { key: "poNumbers", label: "PO / Quote #" },
        { key: "itemCount", label: "Items", sortable: true },
        { key: "grandTotal", label: "Grand Total", sortable: true, thClass: "cart-total-th", tdClass: "cart-total-td" },
        { key: "actions", label: "" }
      ],
      deletedSortBy: "dateDeleted",
      deletedSortDesc: true,
      // Recently Deleted Orders / Quotes (per-tab soft-delete + restore).
      deletedOrders: [],
      loadingDeletedOrders: false,
      showDeletedOrders: false,
      deletedQuotes: [],
      loadingDeletedQuotes: false,
      showDeletedQuotes: false,
      // Bulk-clear selection + "clear before" date for the Orders/Quotes tabs.
      selectedOrderIds: [],
      ordersClearBeforeDate: null,
      // Orders-tab filter chooser: which filters are currently shown, plus the
      // <select> binding used to add one. Inactive filters keep empty values in
      // historyOrdersFilters, so filteredHistoryOrders simply ignores them.
      activeOrderFilters: [],
      orderFilterPick: "",
      selectedQuoteIds: [],
      quotesClearBeforeDate: null
    };
  },
  methods: {
    // ===== ORDERS FILTER CHOOSER =====
    // Human label for a filter key (used by the dropdown options + field).
    orderFilterLabel(key) {
      var def = (this.orderFilterDefs || []).find(function (d) { return d.key === key; });
      return def ? def.label : key;
    },
    // Add the picked filter to the visible set, then reset the dropdown.
    onAddOrderFilter() {
      var key = this.orderFilterPick;
      if (key && this.activeOrderFilters.indexOf(key) < 0) {
        this.activeOrderFilters.push(key);
      }
      this.orderFilterPick = "";
    },
    // Remove a filter field and clear its value so it no longer filters.
    removeOrderFilter(key) {
      var i = this.activeOrderFilters.indexOf(key);
      if (i >= 0) this.activeOrderFilters.splice(i, 1);
      this.$set(this.historyOrdersFilters, key, key === "date" ? null : "");
    },

    // Resolve the currently selected company ID by checking localStorage
    // first (the canonical source for most of the app), then falling back
    // to common Vuex state shapes. This handles the case where the nav
    // company selector has populated Vuex but localStorage hasn't been
    // mirrored yet — otherwise the page would incorrectly show
    // "Please select a company" even though one is clearly selected.
    resolveCompanyId() {
      var ls = localStorage.companyId;
      if (ls) return String(ls);

      if (this.$store && this.$store.state) {
        var s = this.$store.state;
        var cid =
          s.companyId ||
          s.currentCompanyId ||
          s.selectedCompanyId ||
          (s.company && typeof s.company === "string" ? s.company : null);
        if (cid) return String(cid);

        var cobj =
          s.currentCompany ||
          s.selectedCompany ||
          (s.company && typeof s.company === "object" ? s.company : null) ||
          (s.user && s.user.currentCompany) ||
          (s.user && s.user.selectedCompany);
        if (cobj && cobj.id) return String(cobj.id);
        if (cobj && cobj.companyId) return String(cobj.companyId);
      }

      return "";
    },
    getCartType(cart) {
      if (cart.cartType) {
        return cart.cartType;
      }

      if (cart.discriminator === "Rfq" || cart.discriminator === 1) {
        return "ProcessedQuote";
      }

      return cart.hasPoNumber ? "SavedOrder" : "SavedQuote";
    },
    getCartTypeLabel(cart) {
      const type = this.getCartType(cart);
      return type === "SavedQuote" || type === "ProcessedQuote" ? "Quote" : "Order";
    },

    // ===== FLASH-CONFIRM ROW BINDING =====
    // Returns the CSS class for each <tr> in the saved-tab
    // table. Returns "saved-cart-flash" for the row whose
    // cart.id matches flashedCartId so the row animates a
    // brief amber pulse to confirm the save landed where
    // expected. Other rows return null (no class).
    //
    // Bound to b-table's tbody-tr-class prop. Called once per
    // row per render; flashedCartId is data-reactive so when
    // it clears (after 3s), the row re-renders without the
    // class and the animation stops.
    savedTrClass(item) {
      if (!item) return null;
      if (this.flashedCartId && item.id === this.flashedCartId) {
        return "saved-cart-flash";
      }
      return null;
    },

    // ===== ACTIVE-CART INDICATOR =====
    // True when the saved-cart row IS the user's currently
    // loaded active cart (the one that /cart will show on
    // visit). Drives the "In Cart" disabled badge that
    // replaces Resume on those rows — a saved cart that's
    // already loaded shouldn't offer to load itself again.
    //
    // Compares cart.id against the appropriate localStorage
    // pointer based on cart type:
    //   - Order-type → cartId
    //   - Quote-type → cartIdRFQ
    // (kept as separate fields because a user can have one
    // of each loaded simultaneously.)
    isActiveCart(cart) {
      if (!cart || !cart.id) return false;
      var type = this.getCartType(cart);
      if (type === "SavedQuote" || type === "ProcessedQuote") {
        return this.activeCartIdRFQ === cart.id;
      }
      return this.activeCartId === cart.id;
    },

    // ===== EXPAND-ROW LAZY DETAIL FETCH =====
    // Called from the +/− expand button on each saved-cart
    // row. Wraps b-table's row.toggleDetails with a lazy
    // fetch of the cart's full purchaseOrders payload so the
    // detail panel can show a complete breakdown (Orders >
    // Assemblies > Parts) without bloating the initial cart
    // list response.
    //
    // Caching strategy:
    //   - First expand: kick off getCart, render a "loading"
    //     state in the details panel, then swap to the data.
    //   - Subsequent expand of the same row: cached, instant.
    //   - Toggle to collapse: keeps the cache, so re-expanding
    //     skips the fetch.
    //
    // The fetch is fire-and-forget relative to toggleDetails:
    // we expand the row immediately so the user sees feedback
    // (the "loading…" placeholder) rather than waiting on
    // the network round-trip with an unresponsive button.
    expandSavedCart(cart, row) {
      if (!cart || !cart.id) return;
      var key = String(cart.id);
      var cache = this.cartDetails[key];

      // First time on this row — kick off fetch.
      if (!cache) {
        this.$set(this.cartDetails, key, {
          loading: true,
          error: false,
          data: null,
        });
        var self = this;
        apiService
          .getCart({ id: cart.id })
          .then(function (resp) {
            self.$set(self.cartDetails, key, {
              loading: false,
              error: false,
              data: resp || {},
            });
          })
          .catch(function () {
            self.$set(self.cartDetails, key, {
              loading: false,
              error: true,
              data: null,
            });
          });
      }

      // Toggle the row open/closed. Even on first-fetch we
      // open immediately so the loading state is visible.
      row.toggleDetails();
    },

    // Format helper for currency display in the expanded
    // details panel. Mirrors how /cart formats prices and
    // costs ($X.XX). Falls back to "$0.00" for null/undefined
    // / non-numeric so the layout stays consistent.
    formatCurrency(v) {
      if (v == null || isNaN(Number(v))) return "$0.00";
      return "$" + Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // ===== EXPANDED-ROW PURCHASE ORDER GROUPING =====
    // Walks a cart's purchaseOrders array and groups it the
    // same way /cart's groupedOrders computed does:
    //   1. By PO Number (= Order #) — top level.
    //   2. Within each PO, by machine + assembly — multiple
    //      PurchaseOrder rows that share machine+assembly
    //      get merged into one card so the user doesn't see
    //      "BACK-UP SPIRALS" listed twice when the cart has
    //      two backing rows for the same assembly.
    //   3. Within each machine+assembly group, parts dedupe
    //      by code with quantities summed across backing
    //      rows. Otherwise the same part code appears twice
    //      with separate quantities.
    //
    // This mirrors cart.vue's buildGroupedOrders. Without
    // this normalization the breakdown looks doubled up
    // for any cart that's been through partial-flow saves
    // (the partial save/place flow can produce multiple
    // PurchaseOrder rows for the same machine+assembly).
    //
    // Also handles the parts-data field shape: getCart
    // returns NESTED line items
    //   { part: { code, description, price, quantityPerMachine },
    //     quantity }
    // not flat partCode/partDescription/partPrice. This is
    // different from the placed-order endpoint that
    // Success.vue consumes — the cart endpoint's nested
    // shape is what cart.vue expects, and we mirror that
    // here so the breakdown matches what /cart will show
    // after Resume.
    groupedPurchaseOrders(cart) {
      if (!cart || !cart.id) return [];
      var entry = this.cartDetails[String(cart.id)];
      if (!entry || !entry.data) return [];
      var pos = entry.data.purchaseOrders || [];
      if (pos.length === 0) return [];

      var poGroups = {};
      var poOrder = [];

      pos.forEach(function (order) {
        // PO level — preserve insertion order so Order #1
        // is the first PO seen, matching /cart numbering.
        var num = order.number || "";
        if (!poGroups[num]) {
          poGroups[num] = {
            number: num,
            cost: 0,
            _machineMap: {},
            _machineOrder: [],
          };
          poOrder.push(num);
        }
        var poGroup = poGroups[num];

        // Machine + Assembly level — merge by composite key
        // so duplicate backing rows collapse into one card.
        var machineName =
          (order.assembly && order.assembly.machine && order.assembly.machine.description) || "";
        var assemblyName = (order.assembly && order.assembly.name) || "";
        var mKey = machineName + "||" + assemblyName;

        if (!poGroup._machineMap[mKey]) {
          poGroup._machineMap[mKey] = {
            machineName: machineName,
            assemblyName: assemblyName,
            cost: 0,
            _partsMap: {},
            _partsOrder: [],
          };
          poGroup._machineOrder.push(mKey);
        }
        var mGroup = poGroup._machineMap[mKey];
        mGroup.cost += order.cost || 0;
        poGroup.cost += order.cost || 0;

        // Part dedup level — same code + same machine +
        // same assembly = same line item, sum the
        // quantities. Parts come from getCart with the
        // nested { part: {...}, quantity } shape.
        (order.parts || []).forEach(function (lineItem) {
          if (!lineItem || !lineItem.part) return;
          var code =
            lineItem.part.code ||
            lineItem.part.item ||
            ("__id_" + lineItem.id);
          if (!mGroup._partsMap[code]) {
            mGroup._partsMap[code] = {
              // partId captured here so the rolled-up part
              // carries the lookup key needed by
              // isWearEligible() for the discount chip.
              id: lineItem.part.id || null,
              code: code,
              description: lineItem.part.description || "",
              price: Number(lineItem.part.price) || 0,
              quantityPerMachine: Number(lineItem.part.quantityPerMachine) || 0,
              quantity: Number(lineItem.quantity) || 0,
            };
            mGroup._partsOrder.push(code);
          } else {
            mGroup._partsMap[code].quantity += Number(lineItem.quantity) || 0;
          }
        });
      });

      return poOrder.map(function (num, idx) {
        var po = poGroups[num];
        var machineGroups = po._machineOrder.map(function (mKey) {
          var mg = po._machineMap[mKey];
          var parts = mg._partsOrder.map(function (code) {
            var p = mg._partsMap[code];
            // Pre-compute the line cost (price × qty) since
            // the original cost-per-row was on the
            // PurchaseOrder, not the lineItem, and we
            // collapsed those above.
            return {
              id: p.id,
              code: p.code,
              description: p.description,
              price: p.price,
              quantity: p.quantity,
              cost: p.price * p.quantity,
            };
          });
          return {
            machineName: mg.machineName,
            assemblyName: mg.assemblyName,
            parts: parts,
            cost: mg.cost,
          };
        });
        return {
          orderIndex: idx + 1,
          number: po.number,
          machineGroups: machineGroups,
          cost: po.cost,
        };
      });
    },
    loadCarts() {
      // Clear bulk-select checkboxes on every reload so a selection never
      // points at carts that are no longer in the list.
      this.selectedCartIds = [];
      // Refresh active-cart pointers from localStorage in
      // sync with every cart list reload. Either of these can
      // change behind the scenes (Resume on this page sets
      // them, Place Order / Save for Later in /cart clears
      // them) and the "In Cart" indicator on rows must reflect
      // the current truth.
      this.activeCartId = localStorage.cartId
        ? parseInt(localStorage.cartId, 10) || null
        : null;
      this.activeCartIdRFQ = localStorage.cartIdRFQ
        ? parseInt(localStorage.cartIdRFQ, 10) || null
        : null;

      var companyId = this.resolveCompanyId();
      // Admin all-companies mode: skip the bail and send
      // empty companyId so the backend returns rows for
      // every company. Backend must interpret empty/null
      // companyId as "no filter" — if it doesn't, this
      // returns nothing instead of all, and the fix is a
      // small backend change to skip the company filter
      // when companyId is falsy.
      if (!companyId && !this.allCompaniesMode) {
        this.carts = [];
        this.loading = false;
        return;
      }
      var effectiveCompanyId = this.allCompaniesMode ? "" : companyId;
      this.loading = true;
      apiService.getActiveCarts({ companyId: effectiveCompanyId }).then(function (data) {
        this.carts = data || [];
        this.loading = false;
      }.bind(this)).catch(function () {
        this.carts = [];
        this.loading = false;
      }.bind(this));
    },

    // ===== ACCOUNT SCOPE (customer) =====
    // Flip between "All account orders" (everyone on the selected company)
    // and "Just mine". Persists the choice (shared with the dashboard key)
    // and refetches the Orders + Quotes history so counts + rows stay in
    // lockstep — same refresh pattern as the allCompaniesMode watcher above.
    // The Saved tab is intentionally NOT refetched: saved/in-progress carts
    // are personal drafts and always show the user's own regardless of scope.
    switchAccountScope(scope) {
      if (scope !== "account" && scope !== "mine") return;
      if (this.accountScope === scope) return;
      this.accountScope = scope;
      try {
        localStorage.setItem("jcsteele.dashboard.accountScope", scope);
      } catch (e) { /* localStorage unavailable */ }
      this.historyOrders = [];
      this.historyOrdersCount = 0;
      this.historyQuotes = [];
      this.historyQuotesCount = 0;
      if (this.activeTab === "orders") {
        this.loadHistoryOrders();
      } else if (this.activeTab === "quotes") {
        this.loadHistoryQuotes();
      }
    },

    // ===== TAB SWITCHER =====
    // Lazy-loads the data for non-saved tabs on first
    // visit (so users who only ever use the Saved tab
    // don't pay the cost of the history fetches). Switching
    // back to a previously-loaded tab uses cached data
    // until the page is reloaded.
    // Switch tabs and sync the URL via ?tab=<name>. Saved
    // is the default so it omits the query param entirely
    // (URL stays clean as /account/savedcarts). Orders and
    // Quotes set ?tab=orders / ?tab=quotes respectively so
    // tab state is bookmarkable, shareable, and survives
    // page refresh / browser back-forward.
    //
    // router.replace (not push) — switching tabs shouldn't
    // pollute browser history with one entry per click. A
    // single Back press from any tab returns to whatever
    // page the user was on before SavedCarts.
    // ===== loadWearPartIds =====
    // Fetches THIS company's wear-eligible PartIds (its
    // dbo.Wears = the Account Setup wear list) and builds an
    // object map keyed by partId for O(1) lookup in
    // isWearEligible(). The "2% Discount" chip must reflect
    // the customer's OWN wear parts, not the global
    // wear-eligible catalog — so use the company-scoped
    // endpoint keyed by the resolved companyId. Called on
    // mount and again from the company-change interval below.
    // Failures (no method, no companyId, API error, or a
    // pre-deploy 404 on the new endpoint) all silently leave
    // wearPartIds = {} — graceful degradation.
    loadWearPartIds() {
      var self = this;
      if (!apiService.getCompanyWearPartIds) {
        this.wearPartIds = {};
        return;
      }
      var companyId = this.resolveCompanyId();
      if (!companyId) {
        this.wearPartIds = {};
        return;
      }
      apiService
        .getCompanyWearPartIds(companyId)
        .then(function (data) {
          var map = {};
          (data || []).forEach(function (id) {
            map[id] = true;
          });
          self.wearPartIds = map;
        })
        .catch(function () {
          self.wearPartIds = {};
        });
    },
    // ===== isWearEligible =====
    // Returns true when the part is in the loaded
    // wearPartIds map. Mirrors the helper of the same name
    // on Index_-_Cart.vue and RFQSavedItems.vue. The parts
    // builder above captures `id` on each rolled-up row
    // specifically so this lookup works.
    isWearEligible(part) {
      if (!part) return false;
      var map = this.wearPartIds || {};
      var pid = part.id || part.partId || null;
      if (pid == null) return false;
      return !!map[pid];
    },
    setTab(tabName) {
      this.activeTab = tabName;
      var desiredQuery = (tabName === "saved")
        ? {}
        : { tab: tabName };
      // Skip the router call if the URL already matches —
      // avoids a NavigationDuplicated warning when setTab
      // fires from the route-watcher reading the URL on
      // mount or back/forward navigation.
      var currentTab = this.$route.query.tab || "saved";
      if (currentTab !== tabName) {
        this.$router.replace({ query: desiredQuery }).catch(function () {
          // Soft-swallow NavigationDuplicated edge cases
          // (router throws if the user clicks the same tab
          // twice in quick succession before the previous
          // navigation resolves).
        });
      }
      if (tabName === "orders" && this.historyOrders.length === 0
          && !this.loadingHistoryOrders) {
        this.loadHistoryOrders();
      }
      if (tabName === "quotes" && this.historyQuotes.length === 0
          && !this.loadingHistoryQuotes) {
        this.loadHistoryQuotes();
      }
    },

    // ===== ORDERS TAB DATA =====
    // Same endpoint OrderHistory.vue uses. Pages 1, 25 rows
    // — the tab is a recent-activity glance, not a full
    // history view (the "See full Order History" link
    // routes to the dedicated page for that).
    loadHistoryOrders() {
      this.selectedOrderIds = [];
      var companyId = this.resolveCompanyId();
      // Admin all-companies mode: skip the bail, send empty
      // companyId so the backend returns rows for every
      // company. See loadCarts for the same pattern + the
      // caveat about backend null-companyId handling.
      if (!companyId && !this.allCompaniesMode) {
        this.historyOrders = [];
        this.historyOrdersCount = 0;
        return;
      }
      var effectiveCompanyId = this.allCompaniesMode ? "" : companyId;
      this.loadingHistoryOrders = true;
      var self = this;
      apiService
        .getOrders({
          page: 1,
          per_page: 25,
          sortBy: "dateCreated|desc",
          filter: "",
          poNumber: "",
          companyId: effectiveCompanyId,
          accountWide: this.accountScope === "account",
        })
        .then(function (res) {
          self.historyOrdersCount = res.total || 0;
          // OrderHistory.vue uses res.data[0] — same nested
          // shape, mirror it here. Order list items carry their
          // PO numbers inside purchaseOrders[].purchaseOrderNumber
          // (there's no top-level poNumbers field, unlike the
          // Saved tab's source), so derive a unique poNumbers
          // array per order for the merged Order #/PO # cell and
          // the PO Number filter.
          self.historyOrders = (res.data && res.data[0])
            ? Array.from(res.data[0]).map(function (o) {
                o.poNumbers = Array.from(new Set(
                  (o.purchaseOrders || [])
                    .map(function (po) { return po && po.purchaseOrderNumber; })
                    .filter(function (v) { return v != null && String(v).trim() !== ""; })
                ));
                return o;
              })
            : [];
          self.loadingHistoryOrders = false;
        })
        .catch(function () {
          self.historyOrders = [];
          self.historyOrdersCount = 0;
          self.loadingHistoryOrders = false;
        });
    },
    viewHistoryOrder(id) {
      this.$router.push({
        name: "Order Details",
        params: { id: id },
      });
    },
    // Formats a poNumbers value (array or scalar) for display
    // in the PO # cell. Arrays render as comma-separated;
    // scalars render as-is. Empty/null returns empty string
    // so the cell template can fall back to "—".
    formatPoNumbers(pos) {
      if (pos == null) return "";
      if (Array.isArray(pos)) {
        return pos.filter(function (v) { return v != null && v !== ""; })
                  .join(", ");
      }
      return String(pos);
    },

    // ===== QUOTES TAB DATA =====
    // Mirrors RFQHistory.vue's fetchData — httpClient.get
    // against /cart with cid + paging, response shape is
    // { data: [...], total, current_page, per_page }. The
    // /cart endpoint is the historical-quote read; the
    // saved-cart read uses a different endpoint
    // (apiService.getActiveCarts).
    loadHistoryQuotes() {
      this.selectedQuoteIds = [];
      var companyId = this.resolveCompanyId();
      // Admin all-companies mode: skip the bail, send empty
      // cid so the backend returns rows for every company.
      // See loadCarts for the pattern + caveat.
      if (!companyId && !this.allCompaniesMode) {
        this.historyQuotes = [];
        this.historyQuotesCount = 0;
        return;
      }
      var effectiveCompanyId = this.allCompaniesMode ? "" : companyId;
      this.loadingHistoryQuotes = true;
      var self = this;
      var url =
        "/cart?page=1&per_page=25" +
        "&sort=" + encodeURIComponent("dateCreated|desc") +
        "&cid=" + encodeURIComponent(effectiveCompanyId) +
        (this.accountScope === "account" ? "&accountWide=true" : "");
      httpClient
        .get(url)
        .then(function (response) {
          var res = response.data || {};
          self.historyQuotesCount = res.total || 0;
          // RFQHistory expects res.data as an array — same
          // here. Defensive against the nested-array shape
          // in case the endpoint matches getOrders' response
          // structure.
          var rows = res.data;
          if (rows && Array.isArray(rows[0])) {
            rows = rows[0];
          }
          self.historyQuotes = rows ? Array.from(rows) : [];
          self.loadingHistoryQuotes = false;
        })
        .catch(function () {
          self.historyQuotes = [];
          self.historyQuotesCount = 0;
          self.loadingHistoryQuotes = false;
        });
    },
    viewHistoryQuote(id) {
      this.$router.push({
        name: "RFQ Details",
        params: { id: id },
      });
    },
    // Mirrors RFQHistory.vue's createOrder method —
    // converts an existing historical quote into a new
    // active cart, then routes to /cart.
    createOrderFromQuote(id) {
      var self = this;
      apiService
        .rfqOrder({ id: id })
        .then(function (request) {
          if (request && request.id) {
            localStorage.cartId = request.id;
            self.$router.push({
              name: "Cart",
              params: { id: request.id },
            });
          }
        })
        .catch(function (err) {
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Could not create order from quote." +
              (traceId ? " TraceID: " + traceId : ""),
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    // ===== RESUME ENTRY POINT =====
    // Three paths from this method:
    //
    //   1. No active cart of the same type → resume directly.
    //      Common case; same as the legacy behavior.
    //
    //   2. Active cart of same type IS this saved cart →
    //      no-op. The action button is already showing
    //      "In Cart" (disabled), but the safety net handles
    //      programmatic / edge-case calls.
    //
    //   3. Active cart of same type is a DIFFERENT saved
    //      cart → open the conflict modal so the user can
    //      pick Replace / Merge / Cancel. Without this gate
    //      the click silently swapped the active-cart pointer
    //      and the user lost track of which cart was loaded.
    //
    // Same-type check matters: order-type uses cartId, quote-
    // type uses cartIdRFQ. A user with one of each is fine —
    // the two pointers don't conflict — so resuming a Quote
    // when an Order is loaded is a no-conflict path.
    resumeCart(cart) {
      if (!cart || !cart.id) return;

      var cartType = this.getCartType(cart);
      var isQuote = cartType === "SavedQuote" || cartType === "ProcessedQuote";
      var activeId = isQuote ? this.activeCartIdRFQ : this.activeCartId;

      // No active cart of this type → straight to resume.
      if (!activeId) {
        this.performSwap(cart);
        return;
      }

      // Same cart already active → defensive no-op.
      if (activeId === cart.id) {
        return;
      }

      // Conflict — queue the cart and open the choice modal.
      // The modal renders the active-cart's summary alongside
      // the saved-cart's summary so the user can compare
      // before deciding.
      this.pendingResumeCart = cart;
      this.showResumeChoiceModal = true;
    },

    // ===== CHOICE 1 — REPLACE (legacy behavior) =====
    // Swap the active-cart pointer to point at the just-
    // clicked saved cart. The previously-active cart isn't
    // deleted — it stays in /account/savedcarts and its
    // row immediately re-renders as "Resume" (because
    // activeCartId no longer matches it). Reversible.
    //
    // Called from resumeCart's no-conflict path AND from
    // the modal's "Replace" button.
    performSwap(cart) {
      if (!cart || !cart.id) return;
      localStorage.companyId = cart.companyId;
      localStorage.cartLastActivity = Date.now().toString();

      var cartType = this.getCartType(cart);

      if (cartType === "SavedQuote" || cartType === "ProcessedQuote") {
        localStorage.cartIdRFQ = cart.id;
        this.saveCompanyCartState(cart.companyId);
        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
        // ?openModify=restored-quote tells /cart/rfqsaveditems'
        // mounted hook to open the Modify Quote modal in
        // 'restored-quote' context, surfacing the "Quote
        // Restored Successfully" banner. /cart/rfqsaveditems
        // strips the query param after consuming so a back-
        // nav doesn't replay the modal.
        this.$router.push({
          path: "/cart/rfqsaveditems",
          query: { openModify: "restored-quote" },
        });
      } else {
        localStorage.cartId = cart.id;
        this.saveCompanyCartState(cart.companyId);
        if (this.$cartBus) {
          this.$cartBus.$emit("cart-updated");
        }
        // ?openModify=restored tells /cart's mounted hook to
        // open the Modify Order modal in 'restored' context,
        // surfacing the "Cart Restored Successfully" banner.
        // /cart strips the query param after consuming so a
        // back-nav doesn't replay the modal.
        this.$router.push({
          path: "/cart",
          query: { openModify: "restored" },
        });
      }
    },

    // ===== CHOICE 2 — MERGE =====
    // Combine the saved cart's purchaseOrders with the
    // currently-active cart's purchaseOrders into a single
    // new cart, then delete the two originals. The new cart
    // becomes the active cart so /cart shows everything in
    // one place.
    //
    // Three-step API orchestration (parallels the partial
    // save/place flow in cart.vue):
    //
    //   1. Fetch full cart payloads for both — we need the
    //      hydrated purchaseOrders, and the saved cart from
    //      getActiveCarts only carries summary fields. The
    //      cartDetails cache may already have one of them
    //      from a prior expand, but fetch fresh anyway since
    //      cache staleness here costs us a wrong cart.
    //   2. saveCart({id:0, purchaseOrders: combined, ...})
    //      forces SaveNewCart server-side so the
    //      DoesPartExistInTheCart duplicate check doesn't
    //      run (parts existing in the originals isn't a
    //      problem on a brand-new cart).
    //   3. Delete the two originals so they don't linger as
    //      stale rows in Saved Carts. Soft-fail on each so
    //      the merge succeeds even if a delete fails — a
    //      stale row is recoverable; losing the merge isn't.
    //
    // Then point localStorage at the new cart and navigate.
    //
    // Companies must match between the two carts; if they
    // don't, abort with an error toast. Cross-company merge
    // would silently move parts between companies which is
    // never what the user means.
    confirmMerge() {
      if (this.mergingResume) return;
      var cart = this.pendingResumeCart;
      if (!cart || !cart.id) return;

      var cartType = this.getCartType(cart);
      var isQuote = cartType === "SavedQuote" || cartType === "ProcessedQuote";
      var activeId = isQuote ? this.activeCartIdRFQ : this.activeCartId;

      if (!activeId || activeId === cart.id) {
        // Defensive — should be impossible to reach because
        // the modal only opens when there's a real conflict.
        this.showResumeChoiceModal = false;
        this.pendingResumeCart = null;
        return;
      }

      var self = this;
      self.mergingResume = true;

      Promise.all([
        apiService.getCart({ id: activeId }),
        apiService.getCart({ id: cart.id }),
      ])
        .then(function (results) {
          var activeCart = results[0] || {};
          var savedCart = results[1] || {};

          // Cross-company guard. activeCart.companyId is the
          // truth source — saved cart object's companyId
          // matches the row but could be stale; pull fresh
          // from the hydrated payloads.
          var activeCompanyId = activeCart.companyId;
          var savedCompanyId = savedCart.companyId;
          if (activeCompanyId && savedCompanyId
              && activeCompanyId !== savedCompanyId) {
            return Promise.reject({
              __mergeBlock: true,
              reason: "company_mismatch",
            });
          }

          var combined = []
            .concat(activeCart.purchaseOrders || [])
            .concat(savedCart.purchaseOrders || []);

          if (combined.length === 0) {
            return Promise.reject({
              __mergeBlock: true,
              reason: "empty",
            });
          }

          return apiService
            .saveCart({
              id: 0,
              purchaseOrders: combined,
              companyId: activeCompanyId || savedCompanyId,
              specialInstructions: "",
            })
            .then(function (newCart) {
              if (!newCart || !newCart.id) {
                return Promise.reject({
                  __mergeBlock: true,
                  reason: "no_id",
                });
              }
              // Delete originals after the merged cart
              // exists, so a partial failure leaves both
              // originals intact (no data loss).
              return Promise.all([
                apiService.deleteCart({ id: activeId }).catch(function (e) {
                  console.warn("[Merge] deleteCart active failed:", e);
                }),
                apiService.deleteCart({ id: cart.id }).catch(function (e) {
                  console.warn("[Merge] deleteCart saved failed:", e);
                }),
              ]).then(function () {
                return newCart;
              });
            });
        })
        .then(function (newCart) {
          // Point active-cart pointer at the new cart and
          // navigate. Use the same per-type pointer the
          // resume flow uses.
          if (isQuote) {
            localStorage.cartIdRFQ = newCart.id;
          } else {
            localStorage.cartId = newCart.id;
          }
          localStorage.companyId = newCart.companyId;
          localStorage.cartLastActivity = Date.now().toString();
          self.saveCompanyCartState(newCart.companyId);

          if (self.$cartBus) {
            self.$cartBus.$emit("cart-updated");
          }

          self.mergingResume = false;
          self.showResumeChoiceModal = false;
          self.pendingResumeCart = null;

          self.$bvToast.toast(
            "Carts merged. The combined cart is now your active cart.",
            {
              title: "Carts Merged",
              autoHideDelay: 4000,
              variant: "success",
              solid: true,
            }
          );

          self.$router.push(isQuote ? "/cart/rfqsaveditems" : "/cart");
        })
        .catch(function (err) {
          self.mergingResume = false;
          var msg = "Failed to merge carts. Try again.";
          if (err && err.__mergeBlock) {
            if (err.reason === "company_mismatch") {
              msg = "These carts belong to different companies and can't be merged.";
            } else if (err.reason === "empty") {
              msg = "Both carts are empty — nothing to merge.";
            }
          }
          self.$bvToast.toast(msg, {
            title: "Merge Failed",
            autoHideDelay: 5000,
            variant: "danger",
            solid: true,
          });
        });
    },

    // Modal cancel — clear queued cart, close modal.
    cancelResumeChoice() {
      this.showResumeChoiceModal = false;
      this.pendingResumeCart = null;
    },

    // Modal Replace button — same as direct resume path.
    confirmSwap() {
      var cart = this.pendingResumeCart;
      this.showResumeChoiceModal = false;
      this.pendingResumeCart = null;
      if (cart) this.performSwap(cart);
    },
    saveCompanyCartState(companyId) {
      var saveKey = "cart_company_" + companyId;
      var state = {
        cartId: localStorage.cartId || "",
        cartIdRFQ: localStorage.cartIdRFQ || "",
        poNumber: localStorage.poNumber || "",
        poNumberRFQ: localStorage.poNumberRFQ || "",
        machineId: localStorage.machineId || "",
        machineIdRFQ: localStorage.machineIdRFQ || "",
        assemblyId: localStorage.assemblyId || "",
        assemblyIdRFQ: localStorage.assemblyIdRFQ || ""
      };
      localStorage.setItem(saveKey, JSON.stringify(state));
    },
    confirmDeleteCart(cart) {
      var cartType = this.getCartTypeLabel(cart).toLowerCase();
      this.$bvModal
        .msgBoxConfirm("Delete this saved " + cartType + " cart for " + cart.companyName + "? It moves to Recently Deleted Carts below, where you can restore it.", {
          title: "Delete Saved Cart",
          size: "md",
          buttonSize: "sm",
          okVariant: "dark",
          okTitle: "Yes, delete it",
          cancelVariant: "outline",
          cancelTitle: "No, keep it",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (confirmed) {
            apiService.deleteCart({ id: cart.id }).then(function () {
              if (localStorage.cartId == cart.id) {
                localStorage.cartId = 0;
              }
              if (localStorage.cartIdRFQ == cart.id) {
                localStorage.cartIdRFQ = 0;
              }
              var saveKey = "cart_company_" + cart.companyId;
              localStorage.removeItem(saveKey);

              if (this.$cartBus) {
                this.$cartBus.$emit("cart-updated");
              }
              this.loadCarts();
            }.bind(this)).catch(function () {
              this.$bvToast.toast("Failed to delete cart", {
                title: "Error",
                autoHideDelay: 5000,
                variant: "danger",
                solid: true
              });
            }.bind(this));
          }
        }.bind(this))
        .catch(function () {});
    },
    disabledDate(date) {
      return date > new Date();
    },
    confirmClearBeforeDate() {
      if (!this.clearBeforeDate) return;

      var cutoffDate = new Date(this.clearBeforeDate);
      cutoffDate.setHours(23, 59, 59, 999);

      var cartsToDelete = this.carts.filter(function (c) {
        return new Date(c.dateCreated) <= cutoffDate;
      });

      if (cartsToDelete.length === 0) {
        this.$bvToast.toast("No carts found before the selected date.", {
          title: "No Carts to Clear",
          autoHideDelay: 5000,
          variant: "info",
          solid: true
        });
        return;
      }

      var dateStr = (cutoffDate.getMonth() + 1) + "/" + cutoffDate.getDate() + "/" + cutoffDate.getFullYear();

      this.$bvModal
        .msgBoxConfirm("This moves " + cartsToDelete.length + " saved cart(s) created on or before " + dateStr + " to Recently Deleted Carts below. You can restore them from there.", {
          title: "Clear Old Carts",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes, delete them",
          cancelVariant: "outline",
          cancelTitle: "Cancel",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (confirmed) {
            var self = this;
            var cartIds = cartsToDelete.map(function (c) { return c.id; });

            self.deletingMessage = "Clearing " + cartsToDelete.length + " saved cart(s)...";
            self.showDeletingOverlay = true;

            cartsToDelete.forEach(function (cart) {
              if (localStorage.cartId == cart.id) {
                localStorage.cartId = 0;
              }
              if (localStorage.cartIdRFQ == cart.id) {
                localStorage.cartIdRFQ = 0;
              }
              var saveKey = "cart_company_" + cart.companyId;
              localStorage.removeItem(saveKey);
            });

            apiService.bulkDeleteCarts({ ids: cartIds }).then(function (result) {
              var deletedCount = result && result.deletedCount ? result.deletedCount : cartsToDelete.length;

              if (self.$cartBus) {
                self.$cartBus.$emit("cart-updated");
              }
              self.clearBeforeDate = null;
              self.loadCarts();

              setTimeout(function () {
                self.showDeletingOverlay = false;
                self.$bvToast.toast(deletedCount + " cart(s) deleted successfully.", {
                  title: "Carts Cleared",
                  autoHideDelay: 5000,
                  variant: "success",
                  solid: true
                });
              }, 1500);
            }).catch(function () {
              self.showDeletingOverlay = false;
              self.$bvToast.toast("Failed to delete carts. Please try again.", {
                title: "Error",
                autoHideDelay: 5000,
                variant: "danger",
                solid: true
              });
              self.loadCarts();
            });
          }
        }.bind(this))
        .catch(function () {});
    },
    confirmClearAll() {
      if (!this.carts || this.carts.length === 0) return;

      var self = this;
      var totalCount = this.carts.length;

      this.$bvModal
        .msgBoxConfirm("This moves all " + totalCount + " saved cart(s) to Recently Deleted Carts below. You can restore them from there.", {
          title: "Clear All Carts",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes, delete all",
          cancelVariant: "outline",
          cancelTitle: "Cancel",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (confirmed) {
            var cartIds = self.carts.map(function (c) { return c.id; });

            self.deletingMessage = "Clearing all " + totalCount + " saved cart(s)...";
            self.showDeletingOverlay = true;

            self.carts.forEach(function (cart) {
              if (localStorage.cartId == cart.id) {
                localStorage.cartId = 0;
              }
              if (localStorage.cartIdRFQ == cart.id) {
                localStorage.cartIdRFQ = 0;
              }
              var saveKey = "cart_company_" + cart.companyId;
              localStorage.removeItem(saveKey);
            });

            apiService.bulkDeleteCarts({ ids: cartIds }).then(function (result) {
              var deletedCount = result && result.deletedCount ? result.deletedCount : totalCount;

              if (self.$cartBus) {
                self.$cartBus.$emit("cart-updated");
              }
              self.loadCarts();

              setTimeout(function () {
                self.showDeletingOverlay = false;
                self.$bvToast.toast(deletedCount + " cart(s) deleted successfully.", {
                  title: "All Carts Cleared",
                  autoHideDelay: 5000,
                  variant: "success",
                  solid: true
                });
              }, 1500);
            }).catch(function () {
              self.showDeletingOverlay = false;
              self.$bvToast.toast("Failed to delete carts. Please try again.", {
                title: "Error",
                autoHideDelay: 5000,
                variant: "danger",
                solid: true
              });
              self.loadCarts();
            });
          }
        })
        .catch(function () {});
    },
    // Delete just the checked rows (Saved tab bulk-select). Mirrors the
    // clear-before-date flow but scoped to selectedCartIds.
    clearSelectedCarts() {
      var self = this;
      var ids = this.selectedCartIds.slice();
      if (ids.length === 0) return;
      var cartsToDelete = this.carts.filter(function (c) {
        return ids.indexOf(c.id) !== -1;
      });
      if (cartsToDelete.length === 0) return;

      this.$bvModal
        .msgBoxConfirm("This moves " + cartsToDelete.length + " selected saved cart(s) to Recently Deleted Carts below. You can restore them from there.", {
          title: "Clear Selected Carts",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes, delete them",
          cancelVariant: "outline",
          cancelTitle: "Cancel",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (!confirmed) return;

          self.deletingMessage = "Clearing " + cartsToDelete.length + " saved cart(s)...";
          self.showDeletingOverlay = true;

          cartsToDelete.forEach(function (cart) {
            if (localStorage.cartId == cart.id) { localStorage.cartId = 0; }
            if (localStorage.cartIdRFQ == cart.id) { localStorage.cartIdRFQ = 0; }
            var saveKey = "cart_company_" + cart.companyId;
            localStorage.removeItem(saveKey);
          });

          apiService.bulkDeleteCarts({ ids: ids }).then(function (result) {
            var deletedCount = result && result.deletedCount ? result.deletedCount : cartsToDelete.length;
            if (self.$cartBus) { self.$cartBus.$emit("cart-updated"); }
            self.selectedCartIds = [];
            self.loadCarts();
            setTimeout(function () {
              self.showDeletingOverlay = false;
              self.$bvToast.toast(deletedCount + " cart(s) deleted successfully.", {
                title: "Carts Cleared",
                autoHideDelay: 5000,
                variant: "success",
                solid: true
              });
            }, 1500);
          }).catch(function () {
            self.showDeletingOverlay = false;
            self.$bvToast.toast("Failed to delete carts. Please try again.", {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true
            });
            self.loadCarts();
          });
        })
        .catch(function () {});
    },
    // Single "Clear" button → routes to the right action by priority:
    //   1) checked rows  2) "clear before" date  3) clear all.
    onUnifiedClear() {
      if (this.selectedCartIds.length > 0) {
        this.clearSelectedCarts();
      } else if (this.clearBeforeDate) {
        this.confirmClearBeforeDate();
      } else {
        this.confirmClearAll();
      }
    },
    // ===== BULK CLEAR (Orders / Quotes tabs) =====
    // One context-aware Clear per tab. Priority: checked rows → "before"
    // date → all. Date/all span the FULL history (server-side); checked rows
    // are the visible ones. Everything soft-deletes to the matching Recently
    // Deleted list (restorable). kind = "orders" | "quotes".
    clearHistory(kind) {
      var isOrders = kind === "orders";
      var sel = isOrders ? this.selectedOrderIds : this.selectedQuoteIds;
      var beforeDate = isOrders ? this.ordersClearBeforeDate : this.quotesClearBeforeDate;
      var noun = isOrders ? "order" : "quote";

      if (sel.length > 0) {
        this._clearHistoryConfirm(kind, { ids: sel.slice() },
          sel.length + " selected " + noun + "(s)");
      } else if (beforeDate) {
        var cutoff = new Date(beforeDate);
        cutoff.setHours(23, 59, 59, 999);
        var dateStr = (cutoff.getMonth() + 1) + "/" + cutoff.getDate() + "/" + cutoff.getFullYear();
        this._clearHistoryConfirm(kind, { beforeDate: cutoff.toISOString() },
          "all " + noun + "s created on or before " + dateStr + " (your full history)");
      } else {
        var total = isOrders ? this.historyOrdersCount : this.historyQuotesCount;
        this._clearHistoryConfirm(kind, {},
          "ALL " + total + " " + noun + "(s) — your full " + noun + " history");
      }
    },
    _clearHistoryConfirm(kind, payload, desc) {
      var self = this;
      var isOrders = kind === "orders";
      var listName = isOrders ? "Recently Deleted Orders" : "Recently Deleted Quotes";
      this.$bvModal
        .msgBoxConfirm("This moves " + desc + " to " + listName + " below. You can restore them from there.", {
          title: isOrders ? "Clear Orders" : "Clear Quotes",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes, move them",
          cancelVariant: "outline",
          cancelTitle: "Cancel",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (!confirmed) return;
          var data = {
            companyId: self.allCompaniesMode ? "" : self.resolveCompanyId(),
            accountWide: self.accountScope === "account"
          };
          if (payload.ids) data.ids = payload.ids;
          if (payload.beforeDate) data.beforeDate = payload.beforeDate;

          self.deletingMessage = "Clearing " + (isOrders ? "orders" : "quotes") + "...";
          self.showDeletingOverlay = true;

          var call = isOrders ? apiService.clearOrders(data) : apiService.clearQuotes(data);
          call.then(function (result) {
            var n = result && result.deletedCount != null ? result.deletedCount : 0;
            if (self.$cartBus) self.$cartBus.$emit("cart-updated");
            if (isOrders) {
              self.selectedOrderIds = [];
              self.ordersClearBeforeDate = null;
              self.loadHistoryOrders();
              if (self.showDeletedOrders) self.loadDeletedOrders();
            } else {
              self.selectedQuoteIds = [];
              self.quotesClearBeforeDate = null;
              self.loadHistoryQuotes();
              if (self.showDeletedQuotes) self.loadDeletedQuotes();
            }
            setTimeout(function () {
              self.showDeletingOverlay = false;
              self.$bvToast.toast(n + " " + (isOrders ? "order" : "quote") + "(s) moved to " + listName + ".", {
                title: "Cleared",
                autoHideDelay: 5000,
                variant: "success",
                solid: true
              });
            }, 1200);
          }).catch(function () {
            self.showDeletingOverlay = false;
            self.$bvToast.toast("Failed to clear " + (isOrders ? "orders" : "quotes") + ". Please try again.", {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true
            });
          });
        })
        .catch(function () {});
    },
    // ===== ORDERS: soft-delete + restore (Orders tab) =====
    confirmDeleteOrder(order) {
      var self = this;
      this.$bvModal
        .msgBoxConfirm("Remove order " + order.id + " from your history? It moves to Recently Deleted Orders below, where you can restore it.", {
          title: "Remove Order",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes, remove it",
          cancelVariant: "outline",
          cancelTitle: "Cancel",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (!confirmed) return;
          apiService.deleteOrder({ id: order.id }).then(function () {
            self.loadHistoryOrders();
            if (self.showDeletedOrders) self.loadDeletedOrders();
            self.$bvToast.toast("Order moved to Recently Deleted Orders.", {
              title: "Order Removed", autoHideDelay: 5000, variant: "success", solid: true
            });
          }).catch(function () {
            self.$bvToast.toast("Failed to remove order.", {
              title: "Error", autoHideDelay: 5000, variant: "danger", solid: true
            });
          });
        })
        .catch(function () {});
    },
    toggleDeletedOrders() {
      this.showDeletedOrders = !this.showDeletedOrders;
      if (this.showDeletedOrders && this.deletedOrders.length === 0) {
        this.loadDeletedOrders();
      }
    },
    loadDeletedOrders() {
      this.loadingDeletedOrders = true;
      var companyId = this.resolveCompanyId();
      var self = this;
      apiService.getDeletedOrders({
        companyId: this.allCompaniesMode ? "" : companyId,
        accountWide: this.accountScope === "account"
      }).then(function (res) {
        self.deletedOrders = (res && res.data && res.data[0])
          ? Array.from(res.data[0]).map(function (o) {
              o.poNumbers = Array.from(new Set(
                (o.purchaseOrders || [])
                  .map(function (po) { return po && po.purchaseOrderNumber; })
                  .filter(function (v) { return v != null && String(v).trim() !== ""; })
              ));
              return o;
            })
          : [];
        self.loadingDeletedOrders = false;
      }).catch(function () {
        self.deletedOrders = [];
        self.loadingDeletedOrders = false;
      });
    },
    restoreOrder(order) {
      var self = this;
      self.deletingMessage = "Restoring order...";
      self.showDeletingOverlay = true;
      apiService.restoreOrders({ ids: [order.id] }).then(function () {
        self.loadHistoryOrders();
        self.loadDeletedOrders();
        setTimeout(function () {
          self.showDeletingOverlay = false;
          self.$bvToast.toast("Order restored successfully.", {
            title: "Order Restored", autoHideDelay: 5000, variant: "success", solid: true
          });
        }, 1000);
      }).catch(function () {
        self.showDeletingOverlay = false;
        self.$bvToast.toast("Failed to restore order.", {
          title: "Error", autoHideDelay: 5000, variant: "danger", solid: true
        });
      });
    },
    confirmRestoreAllOrders() {
      if (!this.deletedOrders || this.deletedOrders.length === 0) return;
      var self = this;
      var totalCount = this.deletedOrders.length;
      this.$bvModal
        .msgBoxConfirm("This will restore all " + totalCount + " deleted order(s).", {
          title: "Restore All Orders", size: "md", buttonSize: "sm",
          okVariant: "dark", okTitle: "Yes, restore all",
          cancelVariant: "outline", cancelTitle: "Cancel",
          hideHeaderClose: true, centered: true
        })
        .then(function (confirmed) {
          if (!confirmed) return;
          var ids = self.deletedOrders.map(function (o) { return o.id; });
          self.deletingMessage = "Restoring " + totalCount + " order(s)...";
          self.showDeletingOverlay = true;
          apiService.restoreOrders({ ids: ids }).then(function (result) {
            var restoredCount = result && result.restoredCount ? result.restoredCount : totalCount;
            self.loadHistoryOrders();
            self.loadDeletedOrders();
            setTimeout(function () {
              self.showDeletingOverlay = false;
              self.$bvToast.toast(restoredCount + " order(s) restored successfully.", {
                title: "Orders Restored", autoHideDelay: 5000, variant: "success", solid: true
              });
            }, 1500);
          }).catch(function () {
            self.showDeletingOverlay = false;
            self.$bvToast.toast("Failed to restore orders.", {
              title: "Error", autoHideDelay: 5000, variant: "danger", solid: true
            });
          });
        })
        .catch(function () {});
    },

    // ===== QUOTES: soft-delete + restore (Quotes tab; quotes are carts) =====
    confirmDeleteQuote(quote) {
      var self = this;
      this.$bvModal
        .msgBoxConfirm("Remove quote " + quote.id + " from your history? It moves to Recently Deleted Quotes below, where you can restore it.", {
          title: "Remove Quote",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes, remove it",
          cancelVariant: "outline",
          cancelTitle: "Cancel",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (!confirmed) return;
          apiService.deleteCart({ id: quote.id }).then(function () {
            if (self.$cartBus) self.$cartBus.$emit("cart-updated");
            self.loadHistoryQuotes();
            if (self.showDeletedQuotes) self.loadDeletedQuotes();
            self.$bvToast.toast("Quote moved to Recently Deleted Quotes.", {
              title: "Quote Removed", autoHideDelay: 5000, variant: "success", solid: true
            });
          }).catch(function () {
            self.$bvToast.toast("Failed to remove quote.", {
              title: "Error", autoHideDelay: 5000, variant: "danger", solid: true
            });
          });
        })
        .catch(function () {});
    },
    toggleDeletedQuotes() {
      this.showDeletedQuotes = !this.showDeletedQuotes;
      if (this.showDeletedQuotes && this.deletedQuotes.length === 0) {
        this.loadDeletedQuotes();
      }
    },
    loadDeletedQuotes() {
      this.loadingDeletedQuotes = true;
      var companyId = this.resolveCompanyId();
      var self = this;
      var params = { type: "quotes" };
      if (companyId) params.companyId = companyId;
      apiService.getDeletedCarts(params).then(function (data) {
        self.deletedQuotes = data || [];
        self.loadingDeletedQuotes = false;
      }).catch(function () {
        self.deletedQuotes = [];
        self.loadingDeletedQuotes = false;
      });
    },
    restoreQuote(quote) {
      var self = this;
      self.deletingMessage = "Restoring quote...";
      self.showDeletingOverlay = true;
      apiService.restoreCarts({ ids: [quote.id] }).then(function () {
        if (self.$cartBus) self.$cartBus.$emit("cart-updated");
        self.loadHistoryQuotes();
        self.loadDeletedQuotes();
        setTimeout(function () {
          self.showDeletingOverlay = false;
          self.$bvToast.toast("Quote restored successfully.", {
            title: "Quote Restored", autoHideDelay: 5000, variant: "success", solid: true
          });
        }, 1000);
      }).catch(function () {
        self.showDeletingOverlay = false;
        self.$bvToast.toast("Failed to restore quote.", {
          title: "Error", autoHideDelay: 5000, variant: "danger", solid: true
        });
      });
    },
    confirmRestoreAllQuotes() {
      if (!this.deletedQuotes || this.deletedQuotes.length === 0) return;
      var self = this;
      var totalCount = this.deletedQuotes.length;
      this.$bvModal
        .msgBoxConfirm("This will restore all " + totalCount + " deleted quote(s).", {
          title: "Restore All Quotes", size: "md", buttonSize: "sm",
          okVariant: "dark", okTitle: "Yes, restore all",
          cancelVariant: "outline", cancelTitle: "Cancel",
          hideHeaderClose: true, centered: true
        })
        .then(function (confirmed) {
          if (!confirmed) return;
          var ids = self.deletedQuotes.map(function (c) { return c.id; });
          self.deletingMessage = "Restoring " + totalCount + " quote(s)...";
          self.showDeletingOverlay = true;
          apiService.restoreCarts({ ids: ids }).then(function (result) {
            var restoredCount = result && result.restoredCount ? result.restoredCount : totalCount;
            if (self.$cartBus) self.$cartBus.$emit("cart-updated");
            self.loadHistoryQuotes();
            self.loadDeletedQuotes();
            setTimeout(function () {
              self.showDeletingOverlay = false;
              self.$bvToast.toast(restoredCount + " quote(s) restored successfully.", {
                title: "Quotes Restored", autoHideDelay: 5000, variant: "success", solid: true
              });
            }, 1500);
          }).catch(function () {
            self.showDeletingOverlay = false;
            self.$bvToast.toast("Failed to restore quotes.", {
              title: "Error", autoHideDelay: 5000, variant: "danger", solid: true
            });
          });
        })
        .catch(function () {});
    },
    toggleDeletedCarts() {
      this.showDeletedCarts = !this.showDeletedCarts;
      if (this.showDeletedCarts && this.deletedCarts.length === 0) {
        this.loadDeletedCarts();
      }
    },
    loadDeletedCarts() {
      this.loadingDeleted = true;
      var companyId = this.resolveCompanyId();
      apiService.getDeletedCarts(companyId ? { companyId: companyId, type: "saved" } : { type: "saved" }).then(function (data) {
        this.deletedCarts = data || [];
        this.loadingDeleted = false;
      }.bind(this)).catch(function () {
        this.deletedCarts = [];
        this.loadingDeleted = false;
      }.bind(this));
    },
    restoreCart(cart) {
      var self = this;
      self.deletingMessage = "Restoring cart...";
      self.showDeletingOverlay = true;

      apiService.restoreCarts({ ids: [cart.id] }).then(function () {
        if (self.$cartBus) {
          self.$cartBus.$emit("cart-updated");
        }
        self.loadCarts();
        self.loadDeletedCarts();
        setTimeout(function () {
          self.showDeletingOverlay = false;
          self.$bvToast.toast("Cart restored successfully.", {
            title: "Cart Restored",
            autoHideDelay: 5000,
            variant: "success",
            solid: true
          });
        }, 1000);
      }).catch(function () {
        self.showDeletingOverlay = false;
        self.$bvToast.toast("Failed to restore cart.", {
          title: "Error",
          autoHideDelay: 5000,
          variant: "danger",
          solid: true
        });
      });
    },
    confirmRestoreAll() {
      if (!this.deletedCarts || this.deletedCarts.length === 0) return;
      var self = this;
      var totalCount = this.deletedCarts.length;

      this.$bvModal
        .msgBoxConfirm("This will restore all " + totalCount + " deleted cart(s).", {
          title: "Restore All Carts",
          size: "md",
          buttonSize: "sm",
          okVariant: "dark",
          okTitle: "Yes, restore all",
          cancelVariant: "outline",
          cancelTitle: "Cancel",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (confirmed) {
            var cartIds = self.deletedCarts.map(function (c) { return c.id; });
            self.deletingMessage = "Restoring " + totalCount + " cart(s)...";
            self.showDeletingOverlay = true;

            apiService.restoreCarts({ ids: cartIds }).then(function (result) {
              var restoredCount = result && result.restoredCount ? result.restoredCount : totalCount;
              if (self.$cartBus) {
                self.$cartBus.$emit("cart-updated");
              }
              self.loadCarts();
              self.loadDeletedCarts();
              setTimeout(function () {
                self.showDeletingOverlay = false;
                self.$bvToast.toast(restoredCount + " cart(s) restored successfully.", {
                  title: "Carts Restored",
                  autoHideDelay: 5000,
                  variant: "success",
                  solid: true
                });
              }, 1500);
            }).catch(function () {
              self.showDeletingOverlay = false;
              self.$bvToast.toast("Failed to restore carts.", {
                title: "Error",
                autoHideDelay: 5000,
                variant: "danger",
                solid: true
              });
            });
          }
        })
        .catch(function () {});
    },
    confirmPermanentDelete(cart) {
      var self = this;
      this.$bvModal
        .msgBoxConfirm("This will permanently delete this cart. This cannot be undone.", {
          title: "Permanently Delete Cart",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes, permanently delete",
          cancelVariant: "outline",
          cancelTitle: "Cancel",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (confirmed) {
            apiService.permanentDeleteCarts({ ids: [cart.id] }).then(function () {
              self.loadDeletedCarts();
              self.$bvToast.toast("Cart permanently deleted.", {
                title: "Deleted",
                autoHideDelay: 5000,
                variant: "success",
                solid: true
              });
            }).catch(function () {
              self.$bvToast.toast("Failed to delete cart.", {
                title: "Error",
                autoHideDelay: 5000,
                variant: "danger",
                solid: true
              });
            });
          }
        })
        .catch(function () {});
    },
    confirmPermanentDeleteAll() {
      if (!this.deletedCarts || this.deletedCarts.length === 0) return;
      var self = this;
      var totalCount = this.deletedCarts.length;

      this.$bvModal
        .msgBoxConfirm("This will permanently delete all " + totalCount + " deleted cart(s). This cannot be undone.", {
          title: "Delete All Deleted Carts",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Yes, delete all",
          cancelVariant: "outline",
          cancelTitle: "Cancel",
          hideHeaderClose: true,
          centered: true
        })
        .then(function (confirmed) {
          if (confirmed) {
            var cartIds = self.deletedCarts.map(function (c) { return c.id; });
            self.deletingMessage = "Deleting " + totalCount + " cart(s)...";
            self.showDeletingOverlay = true;

            apiService.permanentDeleteCarts({ ids: cartIds }).then(function () {
              self.loadDeletedCarts();
              setTimeout(function () {
                self.showDeletingOverlay = false;
                self.$bvToast.toast(totalCount + " cart(s) permanently deleted.", {
                  title: "Deleted",
                  autoHideDelay: 5000,
                  variant: "success",
                  solid: true
                });
              }, 1500);
            }).catch(function () {
              self.showDeletingOverlay = false;
              self.$bvToast.toast("Failed to delete carts.", {
                title: "Error",
                autoHideDelay: 5000,
                variant: "danger",
                solid: true
              });
            });
          }
        })
        .catch(function () {});
    }
  }
};
</script>

<style scoped>

.clear-date-label {
  font-size: 13px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
  display: block;
}

.clear-date-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.clear-date-picker {
  width: 220px;
  min-width: 220px;
}

.compact-action-btn {
  width: auto !important;
  min-width: 110px;
  white-space: nowrap;
}

.cart-actions {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.cart-resume-link {
  color: #333;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.cart-resume-link:hover {
  text-decoration: underline;
  color: #333;
}

/* ===== "In Cart" indicator =====
   Replaces the Resume link when a saved cart is the user's
   currently loaded active cart. Visually muted to read as
   non-actionable / informational rather than an inviting
   button. cursor:default + no hover underline reinforces
   the disabled feel. */
.cart-incart-badge {
  color: #777;
  font-weight: 600;
  font-size: 13px;
  cursor: default;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  user-select: none;
  opacity: 0.85;
}

.cart-incart-badge:hover {
  text-decoration: none;
  color: #777;
}

.cart-icon-svg {
  vertical-align: middle;
  flex-shrink: 0;
}

/* All shopping-cart icons → blue (same anchor-blue used for the tabs in
   dark/mid mode). The fill is hard-coded on the <path>, so override it. */
.cart-icon-svg path {
  fill: #1565c0 !important;
}

/* Bulk-select column (first column on the Saved tab): narrow + centered. */
.cart-select-th,
.cart-select-td {
  width: 36px;
  text-align: center;
  vertical-align: middle;
}
.cart-select-all,
.cart-select-row {
  cursor: pointer;
  width: 16px;
  height: 16px;
}

/* History tables (Orders / Quotes) live in a content block the floating
   widgets narrow, so the right-hand columns can clip. Shrink the cells a
   touch and let the long Order # / PO # wrap so the Status / actions column
   stays on-screen instead of being cut off. */
#historyOrdersTable,
#historyQuotesTable {
  font-size: 12px;
}
#historyOrdersTable th,
#historyOrdersTable td,
#historyQuotesTable th,
#historyQuotesTable td {
  padding: 7px 8px;
}
#historyOrdersTable .order-ref-id,
#historyOrdersTable .order-ref-po {
  white-space: normal;
  word-break: break-word;
}

.cart-delete-link {
  color: #c0392b;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
}

.cart-delete-link:hover {
  color: #a93226;
  text-decoration: none;
}

/* ===== EXPAND TOGGLE (layer-1 → layer-2 reveal) =====
   Small + / − button that opens the row-details panel showing
   the secondary fields (Company / Code / Owner). Round shape
   so it reads as "control, not column data." Brand-red on hover
   and when open. */
.cart-expand-th,
.cart-expand-td {
  width: 36px;
  text-align: center;
  padding-left: 8px !important;
  padding-right: 4px !important;
}

.cart-expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  background: #fff;
  border: 1px solid #c0c0c0;
  border-radius: 50%;
  font-weight: 700;
  color: #555;
  cursor: pointer;
  line-height: 1;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cart-expand-btn:hover {
  background: #f3f3f3;
  border-color: #888;
  color: #222;
}

.cart-expand-btn.is-open {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}

.cart-expand-icon {
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  /* Sit minus signs on the same baseline as plus signs — without
     this nudge the − floats too high in the circle. */
  margin-top: -1px;
}

/* ===== PO / QUOTE NUMBER CHIPS =====
   A cart can carry multiple POs (one per Reference the user used
   on the Create page). Rendered as small dark pills inside the
   cell so they read as discrete tokens, not free-form text. */
.cart-po-list {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.cart-po-chip {
  display: inline-block;
  padding: 2px 8px;
  background: #f3f3f3;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  color: #333;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

.cart-po-empty {
  color: #aaa;
  font-style: italic;
  font-size: 12px;
}

/* ===== GRAND TOTAL =====
   Right-aligned + tabular-nums so dollar amounts line up
   visually as the user scans down the column. Slightly heavier
   weight to differentiate it from the other numeric cells
   (item count). */
.cart-total-th {
  text-align: right !important;
}

.cart-total-td {
  text-align: right !important;
}

.cart-total-cell {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #222;
  white-space: nowrap;
}

/* ===== ROW DETAILS PANEL (layer 2) =====
   Appears beneath a row when its + button is clicked. Hosts the
   secondary metadata (Company / Code / Owner) that used to take
   up dedicated columns. Definition-list layout so the labels
   are clearly visually subordinate to the values. */
.cart-details-panel {
  padding: 12px 16px;
  background: #fafafa;
  border-left: 3px solid #c0392b;
  border-radius: 4px;
  margin: 4px 0;
}

.cart-details-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0 32px;
  margin: 0;
}

.cart-details-pair {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 0;
}

.cart-details-pair dt {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #888;
}

.cart-details-pair dd {
  margin: 0;
  font-size: 13px;
  color: #222;
  font-weight: 500;
}

/* On narrow screens the details panel stacks one pair per
   line so labels and values stay readable instead of being
   forced into a single horizontal scroll. */
@media (max-width: 575.98px) {
  .cart-details-list {
    gap: 0;
    flex-direction: column;
  }
  .cart-details-pair {
    border-bottom: 1px dashed #e8e8e8;
  }
  .cart-details-pair:last-child {
    border-bottom: none;
  }
}

.cart-restore-link {
  color: #27ae60;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
}

.cart-restore-link:hover {
  text-decoration: underline;
  color: #1e8449;
}

.deleted-carts-section {
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.deleted-carts-heading {
  font-size: 18px;
  font-weight: 600;
  color: #666;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.deleted-carts-arrow {
  font-size: 12px;
  transition: transform 0.2s;
  display: inline-block;
}

.deleted-carts-arrow.open {
  transform: rotate(180deg);
}

.deleted-carts-count {
  font-weight: 400;
  color: #999;
  font-size: 14px;
}

.deleted-carts-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.deleted-action-btn {
  display: inline-block;
  width: auto !important;
  min-width: 110px;
  white-space: nowrap;
  font-weight: 600;
}

.deleted-restore-btn {
  color: #333 !important;
}

.deleted-restore-btn:hover {
  color: #000 !important;
}

.deleted-delete-btn {
  color: #333 !important;
}

.deleted-delete-btn:hover {
  color: #000 !important;
}

.deleted-carts-table {
  opacity: 0.75;
}

.deleting-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.92);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.deleting-message {
  text-align: center;
}

.deleting-message p {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-top: 16px;
}
/* Recently Deleted Cart Buttons */
.deleted-action-btn {
  display: inline-block;
  width: auto !important;
  min-width: 110px;
  white-space: nowrap;
  font-weight: 600;
  padding: 6px 12px;
}

/* Restore button */
.deleted-restore-btn {
  color: #333 !important;
  border-color: #333 !important;
  background: transparent;
}

.deleted-restore-btn:hover {
  background-color: #333 !important;
  color: #fff !important;
}

/* Delete button */
.deleted-delete-btn {
  color: #c0392b !important;
  border-color: #c0392b !important;
  background: transparent;
}

.deleted-delete-btn:hover {
  background-color: #c0392b !important;
  color: #fff !important;
}

.deleting-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto;
  border: 4px solid #ddd;
  border-top: 4px solid #c0392b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== TABS =====
   Three-tab nav at the top of SavedCarts: Saved /
   Orders / Quotes. Active tab gets a brand-red bottom
   border + bolder text, inactive tabs are muted gray.
   Tab badges show the count of items in each tab. */
/* Outer row that holds the tab buttons (left) and the
   admin-only All Companies toggle (right). Splits horizontal
   space so each side floats to its corner. The bottom border
   moves up here so it spans the full width regardless of
   whether the toggle is rendered. */
.savedcarts-tabs-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 2px solid #e3e3e3;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.savedcarts-tabs {
  display: flex;
  gap: 0;
  flex-wrap: wrap;
}

/* Admin toggle pill. Two segmented buttons sharing a single
   pill-shaped border. Active button gets a brand-red fill;
   inactive stays transparent with subtle text color. Sized
   to align visually with the bottom edge of the tabs. */
.savedcarts-admin-toggle {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d4d4d4;
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 4px;
  font-size: 12px;
}

.savedcarts-toggle-btn {
  background: transparent;
  border: none;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.savedcarts-toggle-btn:hover {
  color: #333;
}

.savedcarts-toggle-btn.is-active {
  background: #c0392b;
  color: #fff;
}

/* ===== ORDERS TAB FILTER ROW =====
   Six-cell grid above the orders table. CSS Grid with
   auto-fit minmax so columns reflow gracefully — six
   inline at desktop widths, fewer per row as the viewport
   narrows, single-column on mobile. Each cell stacks its
   label on top of its input. */
/* ===== ORDERS FILTER — dropdown-driven chooser ===== */
.orders-filter-bar {
  margin: 16px 0 20px;
}
.orders-filter-add {
  margin-bottom: 12px;
}
.orders-filter-select {
  width: 100%;
  max-width: 280px;
  height: 38px;
  padding: 6px 12px;
  border: 1px solid #d4d4d4;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}
.orders-filter-select:focus {
  outline: none;
  border-color: #c0392b;
}
.orders-filter-active {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px 16px;
}
.orders-filter-active .orders-filter-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.orders-filter-remove {
  border: none;
  background: transparent;
  color: #c0392b;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
  margin-left: 6px;
}
.orders-filter-remove:hover {
  color: #8c2a20;
}
.orders-filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px 16px;
  margin: 16px 0 20px;
}

.orders-filter-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.orders-filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #444;
  text-align: center;
  margin: 0;
}

.orders-filter-input {
  width: 100%;
  height: 38px;
  padding: 6px 12px;
  border: 1px solid #d4d4d4;
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  background: #fff;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.orders-filter-input:focus {
  outline: none;
  border-color: #c0392b;
}

/* vue2-datepicker wraps its input in a .mx-datepicker. Make
   the wrapper fill its grid cell so the date picker matches
   the other inputs' width visually. */
.orders-filter-cell .mx-datepicker {
  width: 100%;
}

/* ===== ORDERS STATUS CHIP =====
   Mirrors Index_-_Account.vue's dashboard-list-status pattern
   for visual consistency: green pill for completed orders,
   gray pill for open/in-progress. Inline-flex so it sizes
   to its text rather than spanning the cell width. */
.orders-status-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.orders-status-chip.is-complete {
  background: #e8f5ec;
  color: #1f7a3a;
}

.orders-status-chip.is-open {
  background: #ececec;
  color: #555;
}

.savedcarts-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  font-size: 14px;
  font-weight: 600;
  color: #777;
  cursor: pointer;
  transition: all 0.15s ease;
}

.savedcarts-tab:hover {
  color: #333;
}

.savedcarts-tab.is-active {
  color: #c0392b;
  border-bottom-color: #c0392b;
  font-weight: 700;
}

.savedcarts-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  background: #ececec;
  color: #555;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 700;
}

.savedcarts-tab.is-active .savedcarts-tab-count {
  background: #c0392b;
  color: #fff;
}

.tab-pane-header {
  margin-bottom: 16px;
}

.tab-pane-intro {
  margin: 0;
  color: #666;
  font-size: 13px;
}

.tab-pane-intro a {
  color: #c0392b;
  font-weight: 600;
  text-decoration: none;
}

.tab-pane-intro a:hover {
  text-decoration: underline;
}

.tab-pane-footer-link {
  text-align: right;
  margin: 16px 0 0;
}

.tab-pane-footer-link a {
  color: #c0392b;
  font-weight: 600;
  text-decoration: none;
  font-size: 13px;
}

.tab-pane-footer-link a:hover {
  text-decoration: underline;
}

.cart-actions .table-btn-sm {
  padding: 4px 10px;
  font-size: 12px;
}

/* =====================================================
   SAVED CART EXPANDED — ORDER / ASSEMBLY / PARTS BREAKDOWN
   =====================================================
   Renders below the existing Company/Code metadata when a
   saved cart row is expanded. Visually distinct from the
   row above (light card on grey background, bordered on the
   left) so the user sees this is "what's inside this cart"
   rather than more cart-level metadata.

   Hierarchy is one card per Order #, with assemblies stacked
   inside that card, and a parts table per assembly. Mirrors
   /cart's grouping so the user has continuity between what
   they see here and what they'll see after Resume. */

.saved-cart-orders {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #e8e8e8;
}

.saved-cart-orders-loading,
.saved-cart-orders-empty,
.saved-cart-orders-error {
  font-size: 13px;
  color: #777;
  padding: 8px 4px;
}

.saved-cart-orders-error {
  color: #c0392b;
}

.saved-cart-order-card {
  background: #fff;
  border: 1px solid #e3e3e3;
  border-left: 3px solid #c0392b;
  border-radius: 3px;
  padding: 12px 14px;
  margin-bottom: 14px;
}

.saved-cart-order-card:last-child {
  margin-bottom: 0;
}

.saved-cart-order-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.saved-cart-order-label {
  font-weight: 700;
  font-size: 13px;
  color: #c0392b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.saved-cart-order-meta {
  font-size: 12px;
  color: #555;
  flex: 1 1 auto;
}

.saved-cart-order-meta strong {
  color: #333;
}

.saved-cart-order-cost {
  font-weight: 700;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}

/* Each assembly inside an Order card. Indented slightly so
   the grouping is visually obvious — Order is the container,
   assemblies are children. */
.saved-cart-assembly {
  padding: 8px 0;
}

.saved-cart-assembly + .saved-cart-assembly {
  border-top: 1px dashed #f0f0f0;
  margin-top: 6px;
}

.saved-cart-assembly-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}

.saved-cart-machine {
  font-size: 12px;
  font-weight: 700;
  color: #222;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.saved-cart-assembly-name {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Compact parts table — narrower than /cart's full table,
   read-only (no qty inputs / remove buttons). Just enough
   to identify what's inside before Resume. */
.saved-cart-parts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.saved-cart-parts-table th {
  text-align: left;
  font-weight: 600;
  color: #555;
  padding: 4px 8px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 11px;
}

.saved-cart-parts-table td {
  padding: 5px 8px;
  border-bottom: 1px solid #f4f4f4;
  vertical-align: top;
  color: #333;
}

.saved-cart-parts-table tr:last-child td {
  border-bottom: none;
}

.saved-cart-parts-table .sc-col-qty,
.saved-cart-parts-table .sc-col-price,
.saved-cart-parts-table .sc-col-cost {
  text-align: right;
  white-space: nowrap;
}

.saved-cart-discount-chip {
  display: inline-block;
  background: #c8e6c9;
  color: #1b5e20;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
  vertical-align: middle;
  white-space: nowrap;
}
.saved-cart-parts-table .sc-col-code {
  white-space: nowrap;
  font-family: monospace;
}

.saved-cart-parts-table .sc-col-desc {
  width: 100%;
}

@media (max-width: 768px) {
  .saved-cart-parts-table {
    font-size: 11px;
  }
  .saved-cart-parts-table th,
  .saved-cart-parts-table td {
    padding: 4px 6px;
  }
  .saved-cart-order-header {
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
  }
}

/* =====================================================
   SAVED CART FLASH-CONFIRM ANIMATION
   =====================================================
   Triggered when the user lands on /account/savedcarts
   from /cart's Save flow. The just-saved row pulses with
   an amber background fade so the user can immediately
   confirm "yes, this is the cart I just saved" without
   scanning dates or PO numbers to find it.

   Two-stage animation:
   1) Quick flash IN (0–10%): swing into a strong amber
      tint so the eye is drawn to the row.
   2) Slow fade OUT (10–100%): drift back to transparent
      over the remaining duration so the row settles
      naturally into the rest of the table.

   Total duration ~2.4s — slow enough to register as
   intentional confirmation, fast enough to not feel
   sluggish. flashedCartId is cleared via setTimeout 3s
   after mount which gives the animation a small buffer
   to finish before the class drops off.

   ::v-deep needed because b-table renders the <tr> /
   <td> elements outside the scoped-style boundary of
   this component, so a plain selector wouldn't reach
   them. */
::v-deep tr.saved-cart-flash > td {
  animation: savedCartFlashAmber 2400ms ease-out 1;
}

@keyframes savedCartFlashAmber {
  0% {
    background-color: rgba(255, 193, 7, 0);
  }
  10% {
    background-color: rgba(255, 193, 7, 0.55);
  }
  100% {
    background-color: rgba(255, 193, 7, 0);
  }
}

/* =====================================================
   RESUME CONFLICT MODAL
   =====================================================
   Two large stacked option buttons (Replace / Merge) with
   a hint line under each so the user can distinguish what
   each action does without re-reading the body copy. The
   buttons are full-width and use a left-aligned label +
   hint layout so each option reads like its own card.
   Cancel is a plain text link below the options. */
.resume-choice-modal .resume-choice-intro {
  margin-bottom: 14px;
  color: #444;
  font-size: 14px;
  line-height: 1.4;
}

.resume-choice-modal .resume-choice-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}

.resume-choice-modal .resume-choice-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  padding: 12px 14px;
  text-transform: none;
  letter-spacing: normal;
  white-space: normal;
  line-height: 1.3;
}

.resume-choice-modal .resume-choice-btn strong {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.resume-choice-modal .resume-choice-btn .resume-choice-hint {
  display: block;
  font-size: 12px;
  font-weight: 400;
  margin-top: 4px;
  opacity: 0.85;
  text-transform: none;
  letter-spacing: normal;
}

.resume-choice-modal .resume-choice-cancel-row {
  display: flex;
  justify-content: center;
  padding-top: 6px;
  border-top: 1px solid #f0f0f0;
}

.resume-choice-modal .resume-choice-cancel {
  color: #666;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.resume-choice-modal .resume-choice-cancel:hover {
  color: #333;
  text-decoration: none;
}

/* ===== SAVED CARTS CARD — red top accent =====
   Matches the build pages' card (a 3px brand-red line across the
   top of the rounded white panel). #ao-table is the card; steele's
   white-panel rule styles only its bg / radius / shadow, so this
   descendant selector (plus the scoped data-v attribute) lands the
   accent cleanly on the card's top edge. */
#acctOrders #ao-table {
  border-top: 3px solid #c0392b !important;
}

/* Merged Order # / PO # cell (Orders tab): the order number on top,
   each PO number stacked on its own muted line beneath. */
.order-ref {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.order-ref-id {
  font-weight: 600;
}
.order-ref-po {
  font-size: 11px;
  color: #777;
}

/* History tables (Orders + Quotes) read a touch smaller so the
   denser column set sits comfortably. ::v-deep reaches the b-table
   cells, which render outside this component's scoped boundary. */
::v-deep #historyOrdersTable th,
::v-deep #historyOrdersTable td,
::v-deep #historyQuotesTable th,
::v-deep #historyQuotesTable td {
  font-size: 12.5px;
}

/* Create Order is the primary quote action — brand red to set it
   apart from the gray Review link while sharing the same modern
   icon + text treatment. */
.quote-create-link {
  color: #c0392b;
}
.quote-create-link:hover {
  color: #a93226;
}
</style>